import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

// ── Colors ────────────────────────────────────────────────────────────────────
const C = {
  bg:'#0a0a0c', bg2:'#111114', bg3:'#18181c',
  gold:'#c9a84c', goldL:'#e8c97a',
  sage:'#7aab8a', rose:'#c47a7a', sky:'#7a9ec4', lav:'#9a7ac4',
  text:'#f0ede6', muted:'rgba(240,237,230,0.5)', dim:'rgba(240,237,230,0.22)',
  border:'rgba(201,168,76,0.15)',
};
const serif  = { fontFamily:"'Cormorant Garamond', serif" };
const eyebrow = { fontSize:'10px', letterSpacing:'0.12em', color:C.gold, textTransform:'uppercase', fontWeight:500 };
const card   = { background:C.bg2, border:`0.5px solid ${C.border}`, borderRadius:'10px', padding:'14px 16px', marginBottom:'10px' };
const btnG   = { padding:'8px 16px', background:'rgba(201,168,76,0.1)', border:'0.5px solid rgba(201,168,76,0.4)', borderRadius:'7px', color:C.goldL, fontSize:'12px', cursor:'pointer' };
const btnGhost = { padding:'8px 16px', background:'transparent', border:`0.5px solid ${C.border}`, borderRadius:'7px', color:C.muted, fontSize:'12px', cursor:'pointer' };
const btnS   = a => ({ padding:'7px 14px', background: a ? 'rgba(201,168,76,0.12)' : 'transparent', border:`0.5px solid ${a ? 'rgba(201,168,76,0.4)' : C.border}`, borderRadius:'6px', color: a ? C.goldL : C.muted, fontSize:'12px', cursor:'pointer', marginRight:'6px' });

// ── Canvas utilities ──────────────────────────────────────────────────────────
function genCandles(startPrice, n, vol, drift, forcedPath, seedN = 42) {
  let s = seedN;
  const rng = () => { s = (s * 16807 + 0) % 2147483647; return (s - 1) / 2147483646; };
  const out = []; let price = startPrice;
  for (let i = 0; i < n; i++) {
    const forced = forcedPath && forcedPath[i] !== undefined;
    const open = price;
    const move = forced ? forcedPath[i] : (rng() - 0.5) * vol + drift;
    price = Math.max(price + move, 0.5);
    const h = Math.max(open, price) + rng() * vol * 0.35;
    const l = Math.min(open, price) - rng() * vol * 0.35;
    out.push({ open, high:h, low:l, close:price });
  }
  return out;
}

function p2y(p, minP, maxP, h, pad=30) {
  return pad + (1 - (p - minP) / (maxP - minP)) * (h - pad * 2);
}

function getRange(candles) {
  const prices = candles.flatMap(c => [c.high, c.low]);
  const minP = Math.min(...prices), maxP = Math.max(...prices);
  const pad = (maxP - minP) * 0.12;
  return { minP: minP - pad, maxP: maxP + pad };
}

function drawBackground(ctx, w, h) {
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, w, h);
}

function drawGrid(ctx, w, h, minP, maxP, pad=30, rPad=60) {
  ctx.strokeStyle = 'rgba(240,237,230,0.04)';
  ctx.lineWidth = 0.5;
  for (let i = 0; i <= 5; i++) {
    const y = pad + (i / 5) * (h - pad * 2);
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w - rPad, y); ctx.stroke();
    const price = maxP - (i / 5) * (maxP - minP);
    ctx.fillStyle = 'rgba(240,237,230,0.28)';
    ctx.font = '10px Jost, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('$' + price.toFixed(2), w - rPad + 4, y + 4);
  }
}

function drawCandles(ctx, candles, count, w, h, minP, maxP, pad=30, rPad=60) {
  const usableW = w - rPad;
  const slot = usableW / Math.max(count, 1);
  const cw = Math.max(2, slot * 0.55);
  for (let i = 0; i < count && i < candles.length; i++) {
    const c = candles[i];
    const x = slot * i + slot / 2;
    const bull = c.close >= c.open;
    const col = bull ? C.sage : C.rose;
    const oy = p2y(c.open, minP, maxP, h, pad);
    const cy = p2y(c.close, minP, maxP, h, pad);
    const hy = p2y(c.high, minP, maxP, h, pad);
    const ly = p2y(c.low, minP, maxP, h, pad);
    ctx.strokeStyle = col; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x, hy); ctx.lineTo(x, ly); ctx.stroke();
    ctx.fillStyle = col;
    const top = Math.min(oy, cy);
    ctx.fillRect(x - cw / 2, top, cw, Math.max(Math.abs(cy - oy), 1.5));
  }
}

function hLine(ctx, y, w, color, dash=[], label='', rPad=60) {
  ctx.save();
  ctx.strokeStyle = color; ctx.lineWidth = 1.5;
  ctx.setLineDash(dash);
  ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w - rPad, y); ctx.stroke();
  ctx.setLineDash([]);
  if (label) {
    ctx.fillStyle = color; ctx.font = '10px "DM Mono", monospace'; ctx.textAlign = 'left';
    ctx.fillText(label, w - rPad + 4, y - 3);
  }
  ctx.restore();
}

function fillZone(ctx, y1, y2, w, color, rPad=60) {
  ctx.fillStyle = color;
  ctx.fillRect(0, y1, w - rPad, y2 - y1);
}

// ── Metric card ───────────────────────────────────────────────────────────────
function MetricCard({ label, value, sub, color = C.text }) {
  return (
    <div style={{ ...card, marginBottom:'8px' }}>
      <div style={{ ...eyebrow, marginBottom:'4px' }}>{label}</div>
      <div style={{ fontSize:'20px', fontWeight:500, color, ...serif }}>{value}</div>
      {sub && <div style={{ fontSize:'11px', color:C.muted, marginTop:'2px' }}>{sub}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHART 1 — CASH-SECURED PUT
// ─────────────────────────────────────────────────────────────────────────────
const CSP_SCENARIOS = {
  worthless: { label:'Expires Worthless ✓', drift:0.18, vol:1.2, color:C.sage },
  assigned:  { label:'Assigned at Strike', drift:-0.22, vol:1.0, color:C.gold },
  crash:     { label:'Price Crashes ✗', drift:-0.45, vol:1.4, color:C.rose },
};

function CSPChart() {
  const cvs = useRef(null);
  const [scenario, setScenario] = useState('worthless');
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(true);
  const STRIKE = 47, PREMIUM = 2.10, START = 50, N = 60, CONTRACTS = 1;
  const breakeven = STRIKE - PREMIUM;

  const candles = useMemo(() => {
    const sc = CSP_SCENARIOS[scenario];
    return genCandles(START, N, sc.vol, sc.drift, null, scenario === 'worthless' ? 1 : scenario === 'assigned' ? 2 : 3);
  }, [scenario]);

  const { minP, maxP } = useMemo(() => getRange(candles), [candles]);
  const currentPrice = candles[Math.min(frame, candles.length - 1)]?.close ?? START;
  const dte = Math.max(0, N - frame);
  const intrinsic = Math.max(0, STRIKE - currentPrice);
  const currentPnL = (PREMIUM - intrinsic) * 100 * CONTRACTS;
  const thetaRemaining = PREMIUM * (dte / N);

  useEffect(() => {
    setFrame(0); setPlaying(true);
  }, [scenario]);

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setFrame(f => {
      if (f >= N) { setPlaying(false); return f; }
      return f + 1;
    }), 110);
    return () => clearInterval(id);
  }, [playing]);

  useEffect(() => {
    const canvas = cvs.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = 820, h = 380;
    drawBackground(ctx, w, h);
    drawGrid(ctx, w, h, minP, maxP);

    const strikeY = p2y(STRIKE, minP, maxP, h);
    const beY     = p2y(breakeven, minP, maxP, h);
    const topY    = p2y(maxP, minP, maxP, h);
    const botY    = p2y(minP, minP, maxP, h);

    fillZone(ctx, topY,    strikeY, w, 'rgba(122,171,138,0.07)');
    fillZone(ctx, strikeY, beY,     w, 'rgba(201,168,76,0.07)');
    fillZone(ctx, beY,     botY,    w, 'rgba(196,122,122,0.13)');

    drawCandles(ctx, candles, frame, w, h, minP, maxP);

    hLine(ctx, strikeY, w, C.gold,  [6,3], `Short Put Strike $${STRIKE}`);
    hLine(ctx, beY,     w, C.rose,  [4,2], `Breakeven $${breakeven.toFixed(2)}`);

    ctx.fillStyle = 'rgba(122,171,138,0.6)'; ctx.font = '10px Jost, sans-serif'; ctx.textAlign = 'right';
    ctx.fillText('Profit Zone', w - 64, topY + 16);
    ctx.fillStyle = 'rgba(201,168,76,0.6)';
    ctx.fillText('Assignment Zone', w - 64, (strikeY + beY) / 2 + 4);
    ctx.fillStyle = 'rgba(196,122,122,0.7)';
    ctx.fillText('Loss Zone', w - 64, botY - 8);

    // Theta counter
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(8, 8, 190, 28);
    ctx.fillStyle = C.goldL; ctx.font = '11px Jost, sans-serif'; ctx.textAlign = 'left';
    ctx.fillText(`Θ Premium Remaining: $${(thetaRemaining).toFixed(2)}`, 14, 27);

    // Expiration banner
    if (frame >= N) {
      const won = currentPrice >= STRIKE;
      ctx.fillStyle = won ? 'rgba(122,171,138,0.18)' : 'rgba(196,122,122,0.18)';
      ctx.fillRect(0, 0, w - 60, h);
      ctx.fillStyle = won ? C.sage : C.rose;
      ctx.font = 'bold 18px Jost, sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(won ? `EXPIRED WORTHLESS — +$${(PREMIUM * 100 * CONTRACTS).toFixed(0)} KEPT`
                       : `ASSIGNED — You now own ${CONTRACTS * 100} shares at $${STRIKE}`,
                   (w - 60) / 2, h / 2);
    }
  }, [frame, candles, minP, maxP, thetaRemaining, currentPrice]);

  const status = currentPrice > STRIKE + 1 ? ['ON TRACK ✓', C.sage]
               : currentPrice > STRIKE      ? ['APPROACHING ⚠', C.gold]
               :                              ['IN THE MONEY ✗', C.rose];

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'60% 40%', gap:'12px', alignItems:'start' }}>
        <div>
          <canvas ref={cvs} width={820} height={380} style={{ width:'100%', height:'380px', display:'block', borderRadius:'8px' }} />
        </div>
        <div style={{ paddingLeft:'4px' }}>
          <MetricCard label="Premium Received" value={`$${(PREMIUM * 100 * CONTRACTS).toFixed(0)}`} color={C.gold} />
          <MetricCard label="Strike / Breakeven" value={`$${STRIKE} / $${breakeven.toFixed(2)}`} />
          <MetricCard label="Current Stock Price" value={`$${currentPrice.toFixed(2)}`} color={currentPrice > STRIKE ? C.sage : C.rose} />
          <MetricCard label="Days to Expiration" value={`${dte}`} sub={`of ${N} total`} />
          <MetricCard label="Current P&L" value={`${currentPnL >= 0 ? '+' : ''}$${currentPnL.toFixed(0)}`} color={currentPnL >= 0 ? C.sage : C.rose} />
          <div style={{ ...card, textAlign:'center', marginBottom:'8px' }}>
            <div style={{ fontSize:'14px', fontWeight:600, color:status[1] }}>{status[0]}</div>
          </div>
        </div>
      </div>
      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginTop:'12px' }}>
        {Object.entries(CSP_SCENARIOS).map(([k, sc]) => (
          <button key={k} style={btnS(scenario === k)} onClick={() => setScenario(k)}>{sc.label}</button>
        ))}
        <button style={btnGhost} onClick={() => { setFrame(0); setPlaying(true); }}>↺ Replay</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHART 2 — COVERED CALL
// ─────────────────────────────────────────────────────────────────────────────
const CC_SCENARIOS = {
  expires:  { label:'Call Expires Worthless', drift:0.1, vol:1.1 },
  calledaway:{ label:'Called Away at Strike', drift:0.4, vol:0.9 },
  drops:    { label:'Price Drops', drift:-0.3, vol:1.2 },
};

function CCChart() {
  const cvs = useRef(null);
  const [scenario, setScenario] = useState('expires');
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(true);
  const COST_BASIS = 45, START = 48, STRIKE = 52, PREMIUM = 1.85, N = 50;
  const months = 6;
  const monthlyPrem = PREMIUM * 100;

  const candles = useMemo(() => {
    const sc = CC_SCENARIOS[scenario];
    return genCandles(START, N, sc.vol, sc.drift, null, scenario === 'expires' ? 10 : scenario === 'calledaway' ? 20 : 30);
  }, [scenario]);

  const { minP: rawMin, maxP } = useMemo(() => getRange(candles), [candles]);
  const minP = Math.min(rawMin, COST_BASIS - 2);

  useEffect(() => { setFrame(0); setPlaying(true); }, [scenario]);
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setFrame(f => { if (f >= N) { setPlaying(false); return f; } return f + 1; }), 120);
    return () => clearInterval(id);
  }, [playing]);

  const currentPrice = candles[Math.min(frame, candles.length - 1)]?.close ?? START;
  const elapsed = frame / N;
  const monthsElapsed = Math.floor(elapsed * months);
  const totalPremiumCollected = monthsElapsed * monthlyPrem;
  const effectiveCostBasis = Math.max(0, COST_BASIS * 100 - totalPremiumCollected) / 100;
  const annualizedReturn = ((monthlyPrem * 12) / (COST_BASIS * 100)) * 100;

  useEffect(() => {
    const canvas = cvs.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = 500, h = 380;
    drawBackground(ctx, w, h);
    drawGrid(ctx, w, h, minP, maxP, 30, 60);

    const costY   = p2y(COST_BASIS, minP, maxP, h);
    const strikeY = p2y(STRIKE,     minP, maxP, h);
    const topY    = p2y(maxP, minP, maxP, h);
    const botY    = p2y(minP, minP, maxP, h);

    fillZone(ctx, topY,    strikeY, w, 'rgba(201,168,76,0.08)',  60);
    fillZone(ctx, strikeY, costY,   w, 'rgba(122,171,138,0.09)', 60);
    fillZone(ctx, costY,   botY,    w, 'rgba(196,122,122,0.09)', 60);

    drawCandles(ctx, candles, frame, w, h, minP, maxP, 30, 60);
    hLine(ctx, costY,   w, C.sky,  [4,2], `Cost Basis $${COST_BASIS}`, 60);
    hLine(ctx, strikeY, w, C.rose, [6,3], `Short Call Strike $${STRIKE}`, 60);

    ctx.fillStyle = 'rgba(201,168,76,0.5)'; ctx.font = '10px Jost, sans-serif'; ctx.textAlign = 'right';
    ctx.fillText('Gains Capped', w - 64, topY + 14);
    ctx.fillStyle = 'rgba(122,171,138,0.6)';
    ctx.fillText('Profit Zone', w - 64, (strikeY + costY) / 2 + 4);

    if (frame >= N) {
      ctx.fillStyle = currentPrice >= STRIKE ? 'rgba(201,168,76,0.15)' : 'rgba(122,171,138,0.12)';
      ctx.fillRect(0, 0, w - 60, h);
    }
  }, [frame, candles, minP, maxP, currentPrice]);

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'55% 45%', gap:'12px', alignItems:'start' }}>
        <canvas ref={cvs} width={500} height={380} style={{ width:'100%', height:'380px', borderRadius:'8px' }} />
        <div>
          <div style={{ ...card, marginBottom:'8px' }}>
            <div style={{ ...eyebrow, marginBottom:'8px' }}>Monthly Premium Income</div>
            {Array.from({ length:months }, (_, m) => {
              const active = m < monthsElapsed;
              const w = active ? 100 : m === monthsElapsed ? Math.round((elapsed * months - monthsElapsed) * 100) : 0;
              return (
                <div key={m} style={{ marginBottom:'6px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:C.muted, marginBottom:'3px' }}>
                    <span>Month {m + 1}</span><span style={{ color: active ? C.sage : C.dim }}>{active ? `+$${monthlyPrem.toFixed(0)}` : '—'}</span>
                  </div>
                  <div style={{ height:'6px', background:'rgba(255,255,255,0.05)', borderRadius:'3px', overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${w}%`, background:C.sage, borderRadius:'3px', transition:'width 0.3s' }} />
                  </div>
                </div>
              );
            })}
            <div style={{ borderTop:`0.5px solid ${C.border}`, paddingTop:'8px', marginTop:'8px', display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:'11px', color:C.muted }}>Total Premium</span>
              <span style={{ fontSize:'13px', color:C.sage, fontWeight:500 }}>${totalPremiumCollected.toFixed(0)}</span>
            </div>
          </div>
          <MetricCard label="Effective Cost Basis" value={`$${effectiveCostBasis.toFixed(2)}`} sub={`was $${COST_BASIS}`} color={effectiveCostBasis < COST_BASIS ? C.sage : C.text} />
          <MetricCard label="Annualized Return" value={`${annualizedReturn.toFixed(1)}%`} color={C.gold} />
          {effectiveCostBasis <= 0 && (
            <div style={{ ...card, background:'rgba(201,168,76,0.12)', border:`0.5px solid rgba(201,168,76,0.4)`, textAlign:'center' }}>
              <div style={{ color:C.gold, fontSize:'14px', fontWeight:600 }}>🎉 SHARES NOW FREE</div>
              <div style={{ color:C.muted, fontSize:'11px', marginTop:'4px' }}>Cost basis reached zero through premium collection</div>
            </div>
          )}
        </div>
      </div>
      <div style={{ display:'flex', gap:'8px', marginTop:'12px', flexWrap:'wrap' }}>
        {Object.entries(CC_SCENARIOS).map(([k, sc]) => (
          <button key={k} style={btnS(scenario === k)} onClick={() => setScenario(k)}>{sc.label}</button>
        ))}
        <button style={btnGhost} onClick={() => { setFrame(0); setPlaying(true); }}>↺ Replay</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHART 3 — THE WHEEL
// ─────────────────────────────────────────────────────────────────────────────
function WheelChart() {
  const cvs = useRef(null);
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(100);
  const N = 90, CSP_STRIKE = 47, CC_STRIKE = 52, COST_BASIS = 47 * 100, CSP_PREM = 2.1, CC_PREM = 1.85;

  const candles = useMemo(() => {
    const forced = Array.from({ length: N }, (_, i) => {
      if (i < 30)  return 0.15;
      if (i < 45)  return -0.55;
      if (i < 75)  return 0.22;
      return 0.30;
    });
    return genCandles(50, N, 1.0, 0, forced, 99);
  }, []);
  const { minP, maxP } = useMemo(() => getRange(candles), [candles]);

  const phase = frame < 31 ? 0 : frame < 46 ? 1 : frame < 76 ? 2 : 3;
  const premCollected = (Math.min(frame, 30) > 0 ? CSP_PREM * 100 * Math.floor(Math.min(frame, 30) / 15) : 0)
    + (frame > 45 ? CC_PREM * 100 * Math.floor((Math.min(frame, 75) - 45) / 15) : 0);
  const remainingBasis = Math.max(0, COST_BASIS - premCollected);

  useEffect(() => { setFrame(0); setPlaying(true); }, []);
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setFrame(f => { if (f >= N) { setPlaying(false); return f; } return f + 1; }), speed);
    return () => clearInterval(id);
  }, [playing, speed]);

  useEffect(() => {
    const canvas = cvs.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = 820, h = 400;
    drawBackground(ctx, w, h);
    // Phase bar
    const phases = ['Phase 1: Sell CSP', 'Phase 2: Assigned — Hold + Sell CC', 'Phase 3: Called Away — Restart'];
    const phaseW = (w - 60) / 3;
    phases.forEach((p, i) => {
      const active = i === Math.min(phase, 2);
      ctx.fillStyle = active ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.03)';
      ctx.fillRect(i * phaseW, 0, phaseW - 2, 28);
      ctx.fillStyle = active ? C.gold : C.dim;
      ctx.font = `${active ? 600 : 400} 10px Jost, sans-serif`; ctx.textAlign = 'center';
      ctx.fillText(p, i * phaseW + phaseW / 2, 18);
    });

    const pad = 36;
    drawGrid(ctx, w, h, minP, maxP, pad);
    drawCandles(ctx, candles, frame, w, h, minP, maxP, pad);

    const cspY = p2y(CSP_STRIKE, minP, maxP, h, pad);
    const ccY  = p2y(CC_STRIKE,  minP, maxP, h, pad);
    hLine(ctx, cspY, w, C.gold, [6,3], `CSP Strike $${CSP_STRIKE}`, 60);
    if (frame > 30) hLine(ctx, ccY, w, C.sage, [6,3], `CC Strike $${CC_STRIKE}`, 60);

    // Cost basis bar at bottom
    const barH = 18, barY = h - barH - 2;
    const barW = w - 60;
    ctx.fillStyle = 'rgba(255,255,255,0.05)'; ctx.fillRect(0, barY, barW, barH);
    const pct = remainingBasis / COST_BASIS;
    const barColor = pct > 0.6 ? C.rose : pct > 0.3 ? C.gold : C.sage;
    ctx.fillStyle = barColor; ctx.fillRect(0, barY, barW * pct, barH);
    ctx.fillStyle = C.text; ctx.font = '10px Jost, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(`Remaining Cost Basis: $${remainingBasis.toFixed(0)} of $${COST_BASIS}`, barW / 2, barY + 13);

    // Premium popup
    if (frame === 16 || frame === 31) {
      ctx.fillStyle = C.gold; ctx.font = 'bold 14px Jost, sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`+$${(CSP_PREM * 100).toFixed(0)} CSP Premium`, (w - 60) / 2, h / 3);
    }
    if (frame === 31) {
      ctx.fillStyle = C.rose; ctx.font = 'bold 14px Jost, sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('ASSIGNED — Now own 100 shares', (w - 60) / 2, h / 2);
    }
    if (frame >= 76) {
      ctx.fillStyle = C.goldL; ctx.font = 'bold 14px Jost, sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('CALLED AWAY — Cycle Complete! Restart.', (w - 60) / 2, h / 3);
    }
  }, [frame, candles, minP, maxP, phase, remainingBasis]);

  return (
    <div>
      <canvas ref={cvs} width={820} height={400} style={{ width:'100%', height:'400px', borderRadius:'8px' }} />
      <div style={{ display:'flex', gap:'12px', alignItems:'center', marginTop:'10px', flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:'6px' }}>
          {[['Slow', 200], ['Normal', 100], ['Fast', 40]].map(([l, v]) => (
            <button key={l} style={btnS(speed === v)} onClick={() => setSpeed(v)}>{l}</button>
          ))}
        </div>
        <button style={btnGhost} onClick={() => { setFrame(0); setPlaying(true); }}>↺ Reset</button>
        <button style={btnGhost} onClick={() => setPlaying(p => !p)}>{playing ? '⏸ Pause' : '▶ Play'}</button>
        <span style={{ fontSize:'11px', color:C.muted, marginLeft:'auto' }}>
          Premium Collected: <span style={{ color:C.sage }}>${premCollected.toFixed(0)}</span>
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHART 4 — SPX 0DTE SPREADS
// ─────────────────────────────────────────────────────────────────────────────
const DTE_SCENARIOS = {
  quiet:    { label:'Quiet Day — Easy Win', drift:0.05,  vol:4 },
  volatile: { label:'Volatile Day — Nerve-Wracking', drift:-0.02, vol:10 },
  gapdown:  { label:'Gap Down — Stopped Out', drift:-0.35, vol:8 },
};

function ZeroDTEChart() {
  const cvs = useRef(null);
  const [scenario, setScenario] = useState('quiet');
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(false);
  const N = 78, SHORT_STRIKE = 4990, LONG_STRIKE = 4980, CREDIT = 1.50, SPX_START = 5020;

  const candles = useMemo(() => {
    const sc = DTE_SCENARIOS[scenario];
    return genCandles(SPX_START, N, sc.vol, sc.drift, null, scenario === 'quiet' ? 5 : scenario === 'volatile' ? 6 : 7);
  }, [scenario]);
  const { minP, maxP } = useMemo(() => {
    const p = candles.flatMap(c => [c.high, c.low]);
    const mn = Math.min(...p, SHORT_STRIKE - 20), mx = Math.max(...p, SPX_START + 20);
    const pad = (mx - mn) * 0.08;
    return { minP: mn - pad, maxP: mx + pad };
  }, [candles]);

  useEffect(() => { setFrame(0); setPlaying(false); }, [scenario]);
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setFrame(f => { if (f >= N) { setPlaying(false); return f; } return f + 1; }), 80);
    return () => clearInterval(id);
  }, [playing]);

  const currentPrice = candles[Math.min(frame, candles.length - 1)]?.close ?? SPX_START;
  const timeStr = (() => {
    const totalMins = Math.round((frame / N) * 390);
    const h = Math.floor(totalMins / 60) + 9;
    const m = String(totalMins % 60).padStart(2, '0');
    const suffix = h < 12 ? 'AM' : 'PM';
    return `${h > 12 ? h - 12 : h}:${m} ${suffix}`;
  })();
  const spreadValue = Math.max(0, Math.min(CREDIT, SHORT_STRIKE - Math.min(currentPrice, SHORT_STRIKE)));
  const currentPnL = (CREDIT - spreadValue) * 100;
  const profitable = currentPrice > SHORT_STRIKE;

  useEffect(() => {
    const canvas = cvs.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = 820, h = 380;
    drawBackground(ctx, w, h);
    drawGrid(ctx, w, h, minP, maxP);

    const shortY = p2y(SHORT_STRIKE, minP, maxP, h);
    const longY  = p2y(LONG_STRIKE,  minP, maxP, h);
    const topY   = p2y(maxP, minP, maxP, h);
    const botY   = p2y(minP, minP, maxP, h);

    fillZone(ctx, topY,  shortY, w, 'rgba(122,171,138,0.08)');
    fillZone(ctx, shortY, longY, w, 'rgba(201,168,76,0.09)');
    fillZone(ctx, longY,  botY,  w, 'rgba(196,122,122,0.12)');

    drawCandles(ctx, candles, frame, w, h, minP, maxP);
    hLine(ctx, shortY, w, C.gold, [5,3], `Short Put $${SHORT_STRIKE}`);
    hLine(ctx, longY,  w, C.rose, [5,3], `Long Put $${LONG_STRIKE}`);

    // Spread box
    ctx.strokeStyle = 'rgba(201,168,76,0.5)'; ctx.lineWidth = 1;
    ctx.setLineDash([4,2]);
    ctx.strokeRect(2, longY, w - 62, shortY - longY);
    ctx.setLineDash([]);

    // Clock + SPX price
    ctx.fillStyle = 'rgba(0,0,0,0.7)'; ctx.fillRect(6, 6, 200, 32);
    ctx.fillStyle = C.text; ctx.font = 'bold 13px Jost, sans-serif'; ctx.textAlign = 'left';
    ctx.fillText(`SPX ${currentPrice.toFixed(2)}`, 14, 24);
    ctx.fillStyle = C.muted; ctx.font = '11px Jost, sans-serif';
    ctx.fillText(timeStr, 130, 24);

    if (frame >= N) {
      ctx.fillStyle = profitable ? 'rgba(122,171,138,0.15)' : 'rgba(196,122,122,0.15)';
      ctx.fillRect(0, 0, w - 60, h);
      ctx.fillStyle = profitable ? C.sage : C.rose;
      ctx.font = 'bold 16px Jost, sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(profitable ? `EXPIRED — Full Credit Kept +$${(CREDIT*100).toFixed(0)}`
                              : `ASSIGNED — Loss on spread $${Math.abs(currentPnL).toFixed(0)}`,
                   (w - 60) / 2, h / 2);
    }
  }, [frame, candles, minP, maxP, currentPrice, profitable, timeStr, currentPnL]);

  return (
    <div>
      <canvas ref={cvs} width={820} height={380} style={{ width:'100%', height:'380px', borderRadius:'8px' }} />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'8px', marginTop:'10px' }}>
        <MetricCard label="Entry Credit" value={`$${CREDIT.toFixed(2)}`} color={C.gold} />
        <MetricCard label="Current P&L" value={`${currentPnL >= 0 ? '+' : ''}$${currentPnL.toFixed(0)}`} color={currentPnL >= 0 ? C.sage : C.rose} />
        <MetricCard label="SPX Price" value={currentPrice.toFixed(2)} color={C.text} />
        <MetricCard label="Status" value={profitable ? 'PROFITABLE ✓' : 'AT RISK ⚠'} color={profitable ? C.sage : C.gold} />
      </div>
      <div style={{ display:'flex', gap:'8px', marginTop:'10px', flexWrap:'wrap' }}>
        {Object.entries(DTE_SCENARIOS).map(([k, sc]) => (
          <button key={k} style={btnS(scenario === k)} onClick={() => setScenario(k)}>{sc.label}</button>
        ))}
        <button style={btnG} onClick={() => { setFrame(0); setPlaying(true); }}>▶ Start Simulation</button>
        <button style={btnGhost} onClick={() => setPlaying(p => !p)}>{playing ? '⏸ Pause' : '▶ Resume'}</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHART 5 — SWING TRADING
// ─────────────────────────────────────────────────────────────────────────────
function SwingChart() {
  const cvs = useRef(null);
  const [phase, setPhase] = useState(0);
  const [outcome, setOutcome] = useState(null);

  // Constants
  const ENTRY = 49.0, STOP = 46.5, TARGET = 54.0;

  // Build deterministic candle data: 50-candle uptrend, pullback to ~20MA area, bounce, then extended by outcome
  const BASE_N = 50;
  const baseCandles = useMemo(() => {
    // Uptrend: steady climb with small pullbacks
    const path = Array.from({ length: BASE_N }, (_, i) => {
      if (i < 30) return 0.22;           // steady uptrend
      if (i < 38) return -0.30;          // pullback toward 20 MA
      if (i === 38) return -0.05;        // touch
      return 0.28;                       // bounce + entry candles
    });
    return genCandles(38, BASE_N, 0.6, 0, path, 42);
  }, []);

  const allCandles = useMemo(() => {
    if (outcome === 'win') {
      const winPath = [0.35, 0.28, 0.40, 0.32, 0.38, 0.25, 0.30, 0.22, 0.28, 0.35];
      const ext = genCandles(baseCandles[BASE_N - 1].close, 10, 0.4, 0, winPath, 55);
      return [...baseCandles, ...ext];
    }
    if (outcome === 'loss') {
      const lossPath = [-0.25, -0.38, -0.30, -0.42, -0.28, -0.35];
      const ext = genCandles(baseCandles[BASE_N - 1].close, 6, 0.5, 0, lossPath, 66);
      return [...baseCandles, ...ext];
    }
    return baseCandles;
  }, [baseCandles, outcome]);

  const { minP, maxP } = useMemo(() => {
    const mn = Math.min(...allCandles.map(c => c.low), STOP - 1.5);
    const mx = Math.max(...allCandles.map(c => c.high), TARGET + 1.5);
    const pad = (mx - mn) * 0.1;
    return { minP: mn - pad, maxP: mx + pad };
  }, [allCandles]);

  const calcMA = useCallback((candles, period) =>
    candles.map((_, i) => {
      if (i < period - 1) return null;
      return candles.slice(i - period + 1, i + 1).reduce((s, c) => s + c.close, 0) / period;
    }), []);

  useEffect(() => {
    const canvas = cvs.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = 820, h = 380, rPad = 70, pad = 30;

    drawBackground(ctx, w, h);

    const entryY  = p2y(ENTRY,  minP, maxP, h, pad);
    const stopY   = p2y(STOP,   minP, maxP, h, pad);
    const targetY = p2y(TARGET, minP, maxP, h, pad);
    const topY    = pad;

    // Zones
    if (phase >= 5) fillZone(ctx, entryY, stopY,   w, 'rgba(196,122,122,0.07)', rPad);
    if (phase >= 6) fillZone(ctx, targetY, entryY, w, 'rgba(122,171,138,0.07)', rPad);

    drawGrid(ctx, w, h, minP, maxP, pad, rPad);
    drawCandles(ctx, allCandles, allCandles.length, w, h, minP, maxP, pad, rPad);

    const slot = (w - rPad) / allCandles.length;

    const drawMA = (values, color, lineW = 1.5) => {
      ctx.strokeStyle = color; ctx.lineWidth = lineW; ctx.setLineDash([]);
      ctx.beginPath();
      let started = false;
      values.forEach((v, i) => {
        if (v === null) return;
        const x = slot * i + slot / 2;
        const y = p2y(v, minP, maxP, h, pad);
        if (!started) { ctx.moveTo(x, y); started = true; } else ctx.lineTo(x, y);
      });
      ctx.stroke();
    };

    // Phase 1: 50-day MA (red/slow)
    if (phase >= 1) {
      const ma50 = calcMA(allCandles, 50);
      drawMA(ma50, C.rose, 1.5);
      ctx.fillStyle = C.rose; ctx.font = 'bold 10px Jost, sans-serif'; ctx.textAlign = 'left';
      ctx.fillText('50-day MA', w - rPad + 4, p2y(ma50.find(v => v !== null) || STOP, minP, maxP, h, pad));
    }

    // Phase 2: 20-day MA (gold/medium)
    if (phase >= 2) {
      const ma20 = calcMA(allCandles, 20);
      drawMA(ma20, C.gold, 1.8);
      ctx.fillStyle = C.gold; ctx.font = 'bold 10px Jost, sans-serif'; ctx.textAlign = 'left';
      const lastMa20 = [...ma20].reverse().find(v => v !== null);
      if (lastMa20) ctx.fillText('20-day MA', w - rPad + 4, p2y(lastMa20, minP, maxP, h, pad) - 8);
    }

    // Phase 3: 5-day MA (sky/fast)
    if (phase >= 3) {
      const ma5 = calcMA(allCandles, 5);
      drawMA(ma5, C.sky, 1.5);
      ctx.fillStyle = C.sky; ctx.font = 'bold 10px Jost, sans-serif'; ctx.textAlign = 'left';
      const lastMa5 = [...ma5].reverse().find(v => v !== null);
      if (lastMa5) ctx.fillText('5-day MA', w - rPad + 4, p2y(lastMa5, minP, maxP, h, pad) - 8);
    }

    // Phase 4: entry/stop/target lines
    if (phase >= 4) hLine(ctx, entryY,  w, C.goldL, [6,3], `Entry $${ENTRY.toFixed(2)}`, rPad);
    if (phase >= 5) hLine(ctx, stopY,   w, C.rose,  [4,2], `Stop $${STOP.toFixed(2)}`, rPad);
    if (phase >= 6) hLine(ctx, targetY, w, C.sage,  [4,2], `Target $${TARGET.toFixed(2)}`, rPad);

    // Phase 7: entry annotation circle
    if (phase >= 4) {
      const entryIdx = 38;
      const ex = slot * entryIdx + slot / 2;
      ctx.save();
      ctx.strokeStyle = C.goldL; ctx.lineWidth = 2; ctx.setLineDash([]);
      ctx.beginPath(); ctx.arc(ex, entryY + 12, 10, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = C.goldL; ctx.font = 'bold 11px Jost, sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('↑', ex, entryY + 17);
      ctx.restore();
    }

    // Phase 7: outcome label
    if (phase >= 7 && outcome) {
      const riskAmt  = (ENTRY - STOP).toFixed(2);
      const rewardAmt = (TARGET - ENTRY).toFixed(2);
      const rr = (parseFloat(rewardAmt) / parseFloat(riskAmt)).toFixed(1);
      ctx.fillStyle = 'rgba(0,0,0,0.75)'; ctx.fillRect(8, topY + 4, 250, 52);
      ctx.fillStyle = C.goldL; ctx.font = '11px "DM Mono", monospace'; ctx.textAlign = 'left';
      ctx.fillText(`Risk $${riskAmt} · Reward $${rewardAmt} · R:R 1:${rr}`, 16, topY + 22);
      ctx.fillStyle = outcome === 'win' ? C.sage : C.rose;
      ctx.font = 'bold 13px "DM Mono", monospace';
      ctx.fillText(outcome === 'win'
        ? `TARGET HIT — +$${(parseFloat(rewardAmt) * 100).toFixed(0)}`
        : `STOPPED OUT — -$${(parseFloat(riskAmt) * 100).toFixed(0)}`, 16, topY + 44);
    }
  }, [allCandles, phase, outcome, minP, maxP, calcMA]);

  const PHASES = [
    { label: '1. Price Setup', desc: 'Stock in a clear uptrend — price rising steadily' },
    { label: '2. 50-day MA', desc: 'Slowest MA (red) — the long-term trend direction' },
    { label: '3. 20-day MA', desc: 'Medium MA (gold) — the key support level for swing entries' },
    { label: '4. 5-day MA', desc: 'Fast MA (blue) — shows short-term momentum' },
    { label: '5. Entry Signal', desc: 'Price pulls back to 20 MA and bounces — the entry trigger' },
    { label: '6. Stop Loss', desc: 'Stop set below the recent swing low — risk is defined' },
    { label: '7. Target', desc: 'Target at the prior high / next resistance level' },
    { label: '8. Outcome', desc: 'Play either outcome below' },
  ];

  return (
    <div>
      <canvas ref={cvs} width={820} height={380} style={{ width:'100%', height:'auto', borderRadius:'8px', display:'block' }} />

      {/* Phase description */}
      {PHASES[phase] && (
        <div style={{ background:'rgba(201,168,76,0.06)', borderLeft:`2px solid ${C.gold}`, borderRadius:'6px', padding:'10px 14px', marginTop:'12px', marginBottom:'10px' }}>
          <span style={{ fontSize:'12px', fontWeight:600, color:C.goldL, fontFamily:"'Jost',sans-serif" }}>{PHASES[phase].label} — </span>
          <span style={{ fontSize:'12px', color:'rgba(240,237,230,0.75)', fontFamily:"'Jost',sans-serif" }}>{PHASES[phase].desc}</span>
        </div>
      )}

      {/* Step buttons */}
      <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center' }}>
        {PHASES.map((p, i) => (
          <button key={i} style={btnS(phase >= i)} onClick={() => { setPhase(i); if (i < 7) setOutcome(null); }}>
            {i + 1}. {p.label.split('. ')[1]}
          </button>
        ))}
      </div>

      {/* Outcome buttons */}
      {phase >= 7 && (
        <div style={{ display:'flex', gap:'8px', marginTop:'10px' }}>
          <button style={btnG} onClick={() => setOutcome('win')}>Trade Works — Target Hit ✓</button>
          <button style={{ ...btnGhost, color:C.rose, borderColor:'rgba(196,122,122,0.4)' }} onClick={() => setOutcome('loss')}>Stopped Out ✗</button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHART 6 — RISK MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────────
function RiskMgmtChart() {
  const [account, setAccount] = useState(25000);
  const [riskPct, setRiskPct] = useState(2);
  const [positions, setPositions] = useState([
    { ticker:'AAPL', risk:420 }, { ticker:'KO', risk:380 }, { ticker:'SPY', risk:290 },
  ]);
  const [newTicker, setNewTicker] = useState('');

  const totalRisk = positions.reduce((s, p) => s + p.risk, 0);
  const totalRiskPct = (totalRisk / account * 100).toFixed(1);
  const riskColor = totalRiskPct > 15 ? C.rose : totalRiskPct > 10 ? C.gold : C.sage;
  const maxRisk = account * riskPct / 100;

  const addPos = () => {
    if (!newTicker) return;
    setPositions(ps => [...ps, { ticker: newTicker.toUpperCase(), risk: Math.round(maxRisk * 0.8) }]);
    setNewTicker('');
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
      <div>
        <div style={{ ...eyebrow, marginBottom:'10px' }}>Account Heat Map</div>
        <div style={{ ...card }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'12px' }}>
            <span style={{ fontSize:'12px', color:C.muted }}>Total Account Risk</span>
            <span style={{ fontSize:'14px', color:riskColor, fontWeight:600 }}>{totalRiskPct}%</span>
          </div>
          <div style={{ height:'12px', background:'rgba(255,255,255,0.05)', borderRadius:'6px', overflow:'hidden', marginBottom:'6px' }}>
            <div style={{ height:'100%', width:`${Math.min(Number(totalRiskPct), 20) / 20 * 100}%`, background:riskColor, borderRadius:'6px', transition:'width 0.4s' }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:'10px', color:C.dim }}>
            <span>0%</span><span style={{ color:C.gold }}>10% warn</span><span style={{ color:C.rose }}>15% alert</span><span>20%</span>
          </div>
          {Number(totalRiskPct) > 15 && (
            <div style={{ marginTop:'8px', padding:'6px 10px', background:'rgba(196,122,122,0.1)', border:`0.5px solid rgba(196,122,122,0.3)`, borderRadius:'6px', fontSize:'11px', color:C.rose }}>
              ⚠ OVEREXPOSED — Consider reducing position sizes
            </div>
          )}
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'8px' }}>
          {positions.map((p, i) => {
            const pct = (p.risk / account * 100);
            const col = pct > 3 ? C.rose : pct > 2 ? C.gold : C.sage;
            return (
              <div key={i} style={{ background: col + '22', border:`0.5px solid ${col}44`, borderRadius:'8px', padding:'10px 14px', minWidth:'80px', textAlign:'center' }}>
                <div style={{ fontSize:'12px', color:col, fontWeight:500 }}>{p.ticker}</div>
                <div style={{ fontSize:'11px', color:C.muted }}>$\{p.risk}</div>
                <div style={{ fontSize:'10px', color:col }}>{pct.toFixed(1)}%</div>
              </div>
            );
          })}
        </div>
        <div style={{ display:'flex', gap:'6px', marginTop:'10px' }}>
          <input value={newTicker} onChange={e => setNewTicker(e.target.value)} placeholder="Ticker" style={{ flex:1, padding:'8px 10px', background:C.bg3, border:`0.5px solid ${C.border}`, borderRadius:'6px', color:C.text, fontSize:'12px', outline:'none' }} />
          <button style={btnG} onClick={addPos}>Add</button>
          {positions.length > 0 && <button style={btnGhost} onClick={() => setPositions(p => p.slice(0,-1))}>Remove Last</button>}
        </div>
      </div>

      <div>
        <div style={{ ...eyebrow, marginBottom:'10px' }}>Position Sizing Calculator</div>
        <div style={card}>
          {[
            ['Account Size', account, v => setAccount(Number(v)), 'number'],
            ['Max Risk Per Trade (%)', riskPct, v => setRiskPct(Number(v)), 'range', 1, 5],
          ].map(([label, val, setter, type, min, max]) => (
            <div key={label} style={{ marginBottom:'12px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:C.muted, marginBottom:'4px' }}>
                <span>{label}</span>
                <span style={{ color:C.gold }}>{type === 'range' ? val + '%' : '$' + Number(val).toLocaleString()}</span>
              </div>
              {type === 'range'
                ? <input type="range" min={min} max={max} step={0.5} value={val} onChange={e => setter(e.target.value)} style={{ width:'100%', accentColor:C.gold }} />
                : <input type="number" value={val} onChange={e => setter(e.target.value)} style={{ width:'100%', padding:'6px 10px', background:C.bg3, border:`0.5px solid ${C.border}`, borderRadius:'6px', color:C.text, fontSize:'12px', outline:'none' }} />
              }
            </div>
          ))}
          <div style={{ borderTop:`0.5px solid ${C.border}`, paddingTop:'10px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
            <div style={{ background:C.bg3, borderRadius:'7px', padding:'10px' }}>
              <div style={{ fontSize:'10px', color:C.muted, marginBottom:'3px' }}>Max $ Risk</div>
              <div style={{ fontSize:'16px', color:C.gold, fontWeight:500 }}>${maxRisk.toFixed(0)}</div>
            </div>
            <div style={{ background:C.bg3, borderRadius:'7px', padding:'10px' }}>
              <div style={{ fontSize:'10px', color:C.muted, marginBottom:'3px' }}>Per Trade</div>
              <div style={{ fontSize:'16px', color:C.text, fontWeight:500 }}>{riskPct}%</div>
            </div>
          </div>
          <div style={{ marginTop:'10px', padding:'8px 10px', background:'rgba(122,171,138,0.08)', borderRadius:'6px', fontSize:'11px', color:C.muted, lineHeight:1.5 }}>
            💡 Most professional sellers risk no more than 2–3% of account per trade. At $25k, that's $500–750 per position.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHART 7 — TRADING PSYCHOLOGY
// ─────────────────────────────────────────────────────────────────────────────
const PSYCH_STAGES = [
  { name:'Optimism',     x:0.05, y:0.35, trader:'Market looks good — I\'ll start small', expert:'Good time to sell puts on quality stocks' },
  { name:'Excitement',   x:0.14, y:0.20, trader:'I\'m making money! Adding more!', expert:'Be careful — premiums may be getting thin' },
  { name:'Euphoria',     x:0.25, y:0.08, trader:'I\'m a genius! All in!', expert:'Reduce size and take profits — this is the danger zone' },
  { name:'Anxiety',      x:0.38, y:0.30, trader:'Why is it going down?', expert:'Normal pullback — stick to your plan' },
  { name:'Denial',       x:0.48, y:0.45, trader:'It\'ll come back', expert:'Time to check your stops are in place' },
  { name:'Fear',         x:0.58, y:0.58, trader:'Should I get out?', expert:'If you sized correctly, you can hold through this' },
  { name:'Panic',        x:0.67, y:0.70, trader:'SELL EVERYTHING', expert:'The worst time to make decisions. Do nothing.' },
  { name:'Capitulation', x:0.75, y:0.82, trader:'I\'m done with trading', expert:'This is actually near the bottom' },
  { name:'Despair',      x:0.82, y:0.88, trader:'I\'ve lost everything', expert:'Great time to sell puts — premium is elevated' },
  { name:'Hope',         x:0.90, y:0.65, trader:'Maybe it\'s recovering?', expert:'Start scaling back in slowly' },
];

function PsychologyChart() {
  const cvs = useRef(null);
  const [dotPos, setDotPos] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [selected, setSelected] = useState(null);
  const rafRef = useRef(null);
  const posRef = useRef(0);

  useEffect(() => {
    if (playing) {
      const step = () => {
        posRef.current = (posRef.current + 0.003) % 1;
        setDotPos(posRef.current);
        rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]);

  useEffect(() => {
    const canvas = cvs.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = 820, h = 300;
    ctx.fillStyle = C.bg; ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(240,237,230,0.04)'; ctx.lineWidth = 0.5;
    for (let i = 0; i < 5; i++) {
      const y = h * 0.05 + (h * 0.9 / 4) * i;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    }

    // Draw curve through stage points
    const pts = PSYCH_STAGES.map(s => ({ x: s.x * w, y: s.y * h }));
    ctx.strokeStyle = 'rgba(201,168,76,0.25)'; ctx.lineWidth = 2; ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length - 1; i++) {
      const mx = (pts[i].x + pts[i+1].x) / 2, my = (pts[i].y + pts[i+1].y) / 2;
      ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
    }
    ctx.lineTo(pts[pts.length-1].x, pts[pts.length-1].y);
    ctx.stroke();

    // Stage dots
    PSYCH_STAGES.forEach((s, i) => {
      const active = selected === i;
      ctx.beginPath(); ctx.arc(s.x * w, s.y * h, active ? 6 : 4, 0, Math.PI * 2);
      ctx.fillStyle = active ? C.gold : 'rgba(201,168,76,0.4)'; ctx.fill();
      ctx.fillStyle = active ? C.gold : C.muted; ctx.font = `${active ? 600 : 400} 10px Jost, sans-serif`; ctx.textAlign = 'center';
      ctx.fillText(s.name, s.x * w, s.y * h - 12);
    });

    // Animated dot
    const idx = dotPos * (PSYCH_STAGES.length - 1);
    const lo = Math.floor(idx), hi = Math.min(lo + 1, PSYCH_STAGES.length - 1);
    const t = idx - lo;
    const dx = PSYCH_STAGES[lo].x * w + (PSYCH_STAGES[hi].x - PSYCH_STAGES[lo].x) * w * t;
    const dy = PSYCH_STAGES[lo].y * h + (PSYCH_STAGES[hi].y - PSYCH_STAGES[lo].y) * h * t;
    ctx.beginPath(); ctx.arc(dx, dy, 8, 0, Math.PI * 2);
    ctx.fillStyle = C.goldL; ctx.fill();
    ctx.beginPath(); ctx.arc(dx, dy, 12, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(232,201,122,0.3)'; ctx.lineWidth = 2; ctx.stroke();
  }, [dotPos, selected]);

  const stage = PSYCH_STAGES[selected] || PSYCH_STAGES[Math.round(dotPos * (PSYCH_STAGES.length - 1))];

  return (
    <div>
      <canvas ref={cvs} width={820} height={300} style={{ width:'100%', height:'300px', borderRadius:'8px', cursor:'pointer' }}
        onClick={e => {
          const rect = cvs.current.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width;
          const y = (e.clientY - rect.top) / rect.height;
          const closest = PSYCH_STAGES.reduce((best, s, i) => {
            const d = Math.hypot(s.x - x, s.y - y);
            return d < best.d ? { d, i } : best;
          }, { d: Infinity, i: 0 });
          if (closest.d < 0.06) setSelected(closest.i);
        }}
      />
      {stage && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'10px' }}>
          <div style={card}>
            <div style={{ ...eyebrow, marginBottom:'4px' }}>What traders say at "{stage.name}"</div>
            <div style={{ fontSize:'14px', color:C.text, fontStyle:'italic', ...serif }}>"{stage.trader}"</div>
          </div>
          <div style={{ ...card, background:'rgba(201,168,76,0.06)', border:`0.5px solid rgba(201,168,76,0.2)` }}>
            <div style={{ ...eyebrow, marginBottom:'4px', color:C.gold }}>The disciplined trader</div>
            <div style={{ fontSize:'14px', color:C.goldL, ...serif }}>"{stage.expert}"</div>
          </div>
        </div>
      )}
      <div style={{ display:'flex', gap:'8px', marginTop:'8px', flexWrap:'wrap' }}>
        <button style={btnG} onClick={() => setPlaying(p => !p)}>{playing ? '⏸ Pause' : '▶ Animate Cycle'}</button>
        {PSYCH_STAGES.map((s, i) => (
          <button key={i} style={btnS(selected === i)} onClick={() => { setSelected(i); posRef.current = i / (PSYCH_STAGES.length - 1); setDotPos(posRef.current); }}>{s.name}</button>
        ))}
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// CHART 9 — READING CHARTS
// ─────────────────────────────────────────────────────────────────────────────
const PATTERNS = [
  { name:'Doji',           meaning:'Indecision', type:'neutral',  rel:3, note:'Pause at support or resistance is significant — direction depends on what comes next.' },
  { name:'Hammer',         meaning:'Bullish reversal after downtrend', type:'bullish', rel:4, note:'Long lower wick shows buyers stepped in. Most powerful after a sustained decline.' },
  { name:'Shooting Star',  meaning:'Bearish reversal after uptrend',   type:'bearish', rel:3, note:'Long upper wick shows sellers overwhelmed buyers at the top of a move.' },
  { name:'Bullish Engulf', meaning:'Strong bullish reversal',          type:'bullish', rel:4, note:'The body of the second candle fully engulfs the first. High conviction shift from sellers to buyers.' },
  { name:'Bearish Engulf', meaning:'Strong bearish reversal',          type:'bearish', rel:4, note:'One of the most reliable reversal signals at the top of a trend.' },
  { name:'Morning Star',   meaning:'Three-candle bullish reversal',    type:'bullish', rel:5, note:'Requires patience — three candles. The gap-down and gap-up make this reliable. I watch for these at key support.' },
  { name:'Evening Star',   meaning:'Three-candle bearish reversal',    type:'bearish', rel:4, note:'Mirror of the morning star at tops. The middle candle is the star — small body, gap both sides.' },
  { name:'Spinning Top',   meaning:'Indecision, low conviction',       type:'neutral',  rel:2, note:'Small body with upper and lower wicks. Neither side won. Watch for what happens next.' },
  { name:'Inside Bar',     meaning:'Consolidation, breakout coming',   type:'neutral',  rel:3, note:'Price coiling inside the prior bar. A breakout above or below is a directional signal.' },
];

function PatternMiniCanvas({ pattern }) {
  const cvs = useRef(null);
  useEffect(() => {
    const canvas = cvs.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = 120, h = 80;
    ctx.fillStyle = C.bg2; ctx.fillRect(0, 0, w, h);
    const mid = w / 2, midy = h * 0.5;
    const n = pattern.name;
    const draw = (x, open, close, high, low, color) => {
      const scale = h * 0.35;
      const oy = midy - open * scale, cy = midy - close * scale;
      const hy = midy - high * scale, ly = midy - low * scale;
      ctx.strokeStyle = color; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x, hy); ctx.lineTo(x, ly); ctx.stroke();
      ctx.fillStyle = color;
      ctx.fillRect(x - 7, Math.min(oy, cy), 14, Math.max(Math.abs(cy - oy), 2));
    };
    if (n === 'Doji')           { draw(mid, 0, 0.01, 0.6, -0.6, C.muted); }
    else if (n === 'Hammer')    { draw(mid - 15, 0.2, 0.3, 0.35, -0.7, C.sage); draw(mid + 15, 0.2, 0.3, 0.35, -0.7, C.sage); }
    else if (n === 'Shooting Star') { draw(mid, 0.3, 0.2, 0.9, 0.15, C.rose); }
    else if (n === 'Bullish Engulf') { draw(mid - 14, 0.3, -0.2, 0.35, -0.25, C.rose); draw(mid + 14, -0.3, 0.5, 0.55, -0.35, C.sage); }
    else if (n === 'Bearish Engulf') { draw(mid - 14, -0.2, 0.3, 0.35, -0.25, C.sage); draw(mid + 14, 0.4, -0.4, 0.45, -0.45, C.rose); }
    else if (n === 'Morning Star')  { draw(mid - 25, 0.2, -0.3, 0.25, -0.35, C.rose); draw(mid, -0.4, -0.35, -0.3, -0.5, C.muted); draw(mid + 25, -0.35, 0.2, 0.25, -0.4, C.sage); }
    else if (n === 'Evening Star')  { draw(mid - 25, -0.2, 0.3, 0.35, -0.25, C.sage); draw(mid, 0.4, 0.35, 0.55, 0.3, C.muted); draw(mid + 25, 0.35, -0.2, 0.4, -0.25, C.rose); }
    else if (n === 'Spinning Top')  { draw(mid, 0.1, -0.1, 0.6, -0.6, C.muted); }
    else if (n === 'Inside Bar')    { draw(mid - 12, 0.3, -0.2, 0.4, -0.35, C.sage); draw(mid + 12, -0.1, 0.15, 0.25, -0.2, C.muted); }
  }, [pattern.name]);
  return <canvas ref={cvs} width={120} height={80} style={{ width:'100%', height:'80px' }} />;
}

function ReadingChartsModule() {
  const [mode, setMode] = useState('patterns');
  const [expanded, setExpanded] = useState(null);
  const [trendAnswer, setTrendAnswer] = useState(null);
  const trendCvs = useRef(null);

  const trendCandles = useMemo(() => genCandles(40, 30, 1.2, 0.28, null, 55), []);
  const { minP: tmn, maxP: tmx } = useMemo(() => getRange(trendCandles), [trendCandles]);

  useEffect(() => {
    if (mode !== 'trend') return;
    const canvas = trendCvs.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = 820, h = 280;
    drawBackground(ctx, w, h);
    drawGrid(ctx, w, h, tmn, tmx);
    drawCandles(ctx, trendCandles, trendCandles.length, w, h, tmn, tmx);
    if (trendAnswer) {
      const slot = (w - 60) / trendCandles.length;
      let lastLow = null, lastHigh = null;
      trendCandles.forEach((c, i) => {
        if (i % 5 === 0 && i > 0) {
          const x = slot * i + slot / 2;
          const lowY = p2y(c.low, tmn, tmx, h);
          const highY = p2y(c.high, tmn, tmx, h);
          if (lastLow !== null) {
            ctx.strokeStyle = C.sage; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(lastLow.x, lastLow.y); ctx.lineTo(x, lowY); ctx.stroke();
            ctx.fillStyle = C.sage; ctx.font = 'bold 10px Jost'; ctx.textAlign = 'center'; ctx.fillText('HH', x, highY - 6); ctx.fillText('HL', x, lowY + 14);
          }
          lastLow = { x, y: lowY }; lastHigh = { x, y: highY };
        }
      });
      ctx.fillStyle = C.sage + 'cc'; ctx.font = 'bold 16px Jost, sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('✓ Strong Uptrend — Higher Highs, Higher Lows', (w - 60) / 2, 30);
    }
  }, [mode, trendAnswer, trendCandles, tmn, tmx]);

  const expandedPat = expanded !== null ? PATTERNS[expanded] : null;

  return (
    <div>
      <div style={{ display:'flex', gap:'6px', marginBottom:'14px' }}>
        {[['patterns','Pattern Library'],['trend','Identify the Trend']].map(([k, l]) => (
          <button key={k} style={btnS(mode === k)} onClick={() => setMode(k)}>{l}</button>
        ))}
      </div>

      {mode === 'patterns' && (
        <div>
          {expandedPat ? (
            <div style={card}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                <h3 style={{ ...serif, fontSize:'22px', color:C.text }}>{expandedPat.name}</h3>
                <button style={btnGhost} onClick={() => setExpanded(null)}>✕ Back to Grid</button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'200px 1fr', gap:'16px', alignItems:'start' }}>
                <PatternMiniCanvas pattern={expandedPat} />
                <div>
                  <div style={{ marginBottom:'8px' }}>
                    <span style={{ ...eyebrow, marginRight:'8px' }}>Signal</span>
                    <span style={{ fontSize:'13px', color: expandedPat.type === 'bullish' ? C.sage : expandedPat.type === 'bearish' ? C.rose : C.muted }}>
                      {expandedPat.meaning}
                    </span>
                  </div>
                  <div style={{ marginBottom:'8px', display:'flex', gap:'3px' }}>
                    {Array.from({length:5}, (_,i) => (
                      <span key={i} style={{ fontSize:'14px', color: i < expandedPat.rel ? C.gold : C.dim }}>★</span>
                    ))}
                    <span style={{ fontSize:'11px', color:C.muted, marginLeft:'6px' }}>Reliability</span>
                  </div>
                  <div style={{ padding:'10px 12px', background:'rgba(201,168,76,0.07)', borderRadius:'7px', borderLeft:`2px solid ${C.gold}`, fontSize:'13px', color:C.muted, lineHeight:1.6 }}>
                    💬 "{expandedPat.note}"
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'10px' }}>
              {PATTERNS.map((p, i) => (
                <div key={i} style={{ ...card, cursor:'pointer', transition:'border 0.2s' }} onClick={() => setExpanded(i)}>
                  <PatternMiniCanvas pattern={p} />
                  <div style={{ marginTop:'8px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ fontSize:'12px', color:C.text, fontWeight:500 }}>{p.name}</div>
                      <div style={{ fontSize:'10px', color: p.type === 'bullish' ? C.sage : p.type === 'bearish' ? C.rose : C.muted }}>
                        {p.type}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'2px' }}>
                      {Array.from({length:5},(_,j) => <span key={j} style={{ fontSize:'10px', color: j < p.rel ? C.gold : C.dim }}>★</span>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === 'trend' && (
        <div>
          <p style={{ fontSize:'13px', color:C.muted, marginBottom:'10px' }}>Study the 30-candle chart below. What trend do you see?</p>
          <canvas ref={trendCvs} width={820} height={280} style={{ width:'100%', height:'280px', borderRadius:'8px' }} />
          <div style={{ display:'flex', gap:'8px', marginTop:'12px', flexWrap:'wrap' }}>
            {['Strong Uptrend','Weak Uptrend','Downtrend','Sideways/Ranging'].map(t => (
              <button key={t} style={{ ...btnS(trendAnswer !== null), color: trendAnswer && t === 'Strong Uptrend' ? C.sage : trendAnswer && trendAnswer !== t && t !== 'Strong Uptrend' ? C.rose : C.muted }} onClick={() => setTrendAnswer(t)}>
                {trendAnswer && t === 'Strong Uptrend' ? '✓ ' : trendAnswer && trendAnswer === t && t !== 'Strong Uptrend' ? '✗ ' : ''}{t}
              </button>
            ))}
          </div>
          {trendAnswer && (
            <div style={{ marginTop:'10px', padding:'12px', background: trendAnswer === 'Strong Uptrend' ? 'rgba(122,171,138,0.1)' : 'rgba(196,122,122,0.1)', borderRadius:'8px', fontSize:'13px', color: trendAnswer === 'Strong Uptrend' ? C.sage : C.rose }}>
              {trendAnswer === 'Strong Uptrend'
                ? '✓ Correct! This is a strong uptrend — notice higher highs and higher lows on every cycle. The overlaid trendline confirms buyers are consistently stepping in at higher prices.'
                : `✗ Look again — this chart shows a strong uptrend (series of higher highs and higher lows). Your answer was "${trendAnswer}". The trendline on the chart shows the pattern clearly.`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHART MAPPING
// ─────────────────────────────────────────────────────────────────────────────
function PlaceholderChart({ label }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 32px', gap: '16px' }}>
      <div style={{ fontSize: '36px', opacity: 0.4 }}>◌</div>
      <p style={{ ...serif, fontSize: '20px', color: C.gold, margin: 0 }}>Animation Coming Soon</p>
      <p style={{ fontSize: '14px', color: C.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.7, margin: 0, maxWidth: '400px', textAlign: 'center' }}>
        The interactive {label} animation is in development. Read the strategy overview above to understand how and when to use this strategy.
      </p>
    </div>
  );
}

const CHART_MAP = {
  ch02: 'csp',
  ch03: 'cc',
  ch3b: 'wheel',
  ch04: 'zdte',
  ch06: 'csp',
  ch07: 'cc',
  ch08: 'wheel',
};
const CHARTS = [
  // ── TIER 1 — FOUNDATION ───────────────────────────────────────
  { id:'buying',    tier:1, icon:'◐', label:'Buying Calls & Puts', sub:'Start here — understand what you are selling' },
  { id:'csp',       tier:1, icon:'◈', label:'Cash Secured Put & Bull Put Spread', sub:'Get paid to wait',
    combined:true, label1:'Cash Secured Put', label2:'Bull Put Spread',
    component1:CSPChart, component2:() => <PlaceholderChart label="Bull Put Spread" /> },
  { id:'cc',        tier:1, icon:'◉', label:'Covered Call & Bear Call Spread', sub:'Generate income from shares you own',
    combined:true, label1:'Covered Call', label2:'Bear Call Spread',
    component1:CCChart, component2:() => <PlaceholderChart label="Bear Call Spread" /> },
  { id:'wheel',     tier:1, icon:'⊙', label:'The Wheel', sub:'A system for recurring income', component:WheelChart },
  // ── TIER 2 — INTERMEDIATE ─────────────────────────────────────
  { id:'zdte',      tier:2, icon:'◎', label:'0DTE Spread', sub:'Same-day premium — advanced', component:ZeroDTEChart,
    teaser:'Same-day premium collection on SPX index options. Fast decisions, active management, high gamma. The most demanding options strategy — unlocked when your mentor confirms consistent results in the four foundation strategies.' },
  { id:'ironcondor', tier:2, icon:'◇', label:'Iron Condor', sub:'Profit from range-bound markets',
    component:() => <PlaceholderChart label="Iron Condor" />,
    teaser:'Collect premium on both sides of the market simultaneously. A bull put spread below the current price combined with a bear call spread above it. Profits when the stock stays in a defined range. Requires solid understanding of both spread types.' },
  { id:'collar',    tier:2, icon:'○', label:'Collar', sub:'Portfolio protection strategy',
    component:() => <PlaceholderChart label="Collar" />,
    teaser:'Protect shares you own against a major downside move — funded by selling a call above the current price. The call premium offsets the cost of your put protection. Insurance for your portfolio.' },
  { id:'butterfly', tier:2, icon:'◬', label:'Butterfly', sub:'Profit from minimal movement',
    teaser:'A three-strike strategy that profits from minimal price movement. You sell two options at the middle strike and buy one each at the strikes above and below. Precise, defined risk, and rewarding when timed correctly.' },
  { id:'leaps',     tier:2, icon:'∿', label:'LEAPS', sub:'Long term options strategy',
    teaser:'Long-term Equity Anticipation Securities — options with expirations over a year away. Used as stock replacement or long-term directional plays. A completely different relationship with time than short-term options.' },
];

// ─────────────────────────────────────────────────────────────────────────────
// STRATEGY OVERVIEWS
// ─────────────────────────────────────────────────────────────────────────────
const CSP_DATA = {
  tagline: 'Get paid to wait for the price you want.',
  quote: { text: 'The stock market is a device for transferring money from the impatient to the patient.', attr: 'Warren Buffett' },
  why: 'You are bullish on a stock and would genuinely be happy to own it at a lower price. By selling a cash secured put you get paid to wait for the stock to come to you.\n\nIf the stock stays above your strike — you keep the premium and try again at the next expiration. If it falls to your strike — you buy 100 shares at a price you already decided was fair, with a lower cost basis because of the premium you collected.\n\nThis is not speculation. This is a patient, income-generating strategy for traders who do their homework and know what they want to own.',
  right: ['You are bullish or neutral on the stock', 'The stock is at or near a support level', 'Implied volatility is elevated — premium is rich', 'You have cash to buy 100 shares if assigned', 'You genuinely want to own this stock at the strike', 'The stock has good liquidity and options volume', '30–45 days to expiration — theta sweet spot', '🟢 Short strike at 0.15 delta — ~85% probability of expiring worthless. Conservative starting point for beginners.'],
  wrong: ['The stock is in a strong downtrend', 'You do not actually want to own the shares', 'Earnings are approaching — avoid', 'IV is very low — premium is not worth the risk', 'You cannot afford to buy 100 shares if assigned', 'The broader market is in a sharp selloff'],
  risk: 'The stock could fall well below your strike after assignment. You will own shares at your strike price even if the stock is trading much lower. Your only cushion is the premium you collected. This is why the rule exists: only sell puts on stocks you genuinely want to own at the strike price — not just stocks you think will go up.',
  success: 'The stock stays above your strike at expiration. Your put expires worthless. You keep 100% of the premium collected. You repeat the process at the next expiration on the same or a different stock. Over time this income compounds quietly and consistently.\n\nIdeal outcome: sell a $47 put for $2.10 credit ($210 per contract). Stock closes above $47 at expiration. You keep $210 and do it again.',
};

const CC_DATA = {
  tagline: 'Rent out your shares for income each expiration cycle.',
  quote: { text: 'Do not save what is left after spending, but spend what is left after saving.', attr: 'Warren Buffett' },
  why: 'You already own 100 shares of a stock. The stock is moving sideways or you do not expect it to run much higher in the near term. Instead of letting your shares sit idle you rent them out — selling someone the right to buy them at a higher price.\n\nYou collect premium now. If the stock stays below your strike you keep the premium and your shares. Next expiration cycle you do it again. Expiration after expiration your shares generate income whether the stock moves or not.',
  right: ['You already own 100 shares (or multiples of 100)', 'Flat to slightly bearish short-term outlook', 'Stock is near resistance — unlikely to break out', 'IV is elevated — meaningful premium available', 'You are comfortable selling shares at the strike', 'You want income each expiration cycle from existing holdings', '🟢 Short call at 0.15 delta — ~85% probability call expires worthless. Shares stay. Premium kept.'],
  wrong: ['You expect a strong upward move — you will cap your gains', 'You are strongly bullish on the stock', 'IV is very low — not worth capping your upside', 'The stock is in a strong uptrend breaking out', 'You are not willing to sell your shares at the strike'],
  risk: 'If the stock rises above your strike your shares will be called away. You miss every dollar of gain above the strike price. Your upside is permanently capped at strike plus premium for the duration of the trade.\n\nBefore you enter ask yourself: am I comfortable selling my shares at this price? If the answer is no — choose a higher strike or do not sell the call.',
  success: 'The stock stays below your strike at expiration. Your call expires worthless. You keep the premium and your shares. You sell another call at the next expiration. Your shares become an income machine each expiration cycle.\n\nIdeal outcome: own 100 shares at $45. Sell $52 call for $1.85 ($185 per contract). Stock closes at $50. Keep $185. Sell another call at the next expiration.',
  note: 'Options have weekly and monthly expirations. You can sell covered calls as often as each week depending on the expiration you choose.',
};

const WHEEL_DATA = {
  tagline: 'A system for generating income on repeat.',
  quote: { text: 'Compound interest is the eighth wonder of the world. She who understands it, earns it.', attr: 'Albert Einstein' },
  why: 'The Wheel is not a single trade — it is a system. A repeating cycle that generates income from the same stock indefinitely.\n\nStep 1: Sell a Cash Secured Put. Collect premium.\nStep 2a: Put expires worthless — keep premium, repeat.\nStep 2b: Get assigned — you now own 100 shares.\nStep 3: Sell a Covered Call against your shares.\nStep 4a: Call expires worthless — keep premium, repeat.\nStep 4b: Shares get called away — you are back to cash.\nStep 5: Sell another Cash Secured Put. The wheel turns.\n\nAt every step you are collecting premium. The income compounds with each rotation. Over time the strategy can lower your cost basis significantly.',
  right: ['Stock with good implied volatility and liquidity', 'Stock you are comfortable owning long term', 'Neutral to slightly bullish outlook', 'Stable company — not speculative', 'You have capital to be assigned 100 shares', 'The stock has a history of staying range-bound', '🟢 Both legs at 0.15 delta — ~85% probability on CSP and covered call. Maximum consistency for beginners.'],
  wrong: ['Highly volatile or speculative stocks', 'Strong downtrend — assignment traps you in falling shares', 'Earnings approaching — pause the wheel', 'You cannot afford to hold the shares through a downturn', 'Very low IV — premium not worth the commitment'],
  risk: 'The wheel can get stuck. If the stock falls sharply after assignment your shares may be worth significantly less than you paid. Selling covered calls generates income but may not recover your capital if the stock keeps falling.\n\nOnly wheel stocks you are genuinely comfortable holding through a downturn — because that is exactly what might happen.',
  success: 'The wheel keeps turning. Premium flows in at every step. Your cost basis on the shares drops lower with each covered call sold. When the shares are eventually called away you have collected more in premium than you ever could have earned just holding the stock.\n\nIdeal outcome: collect $210 on the CSP, get assigned, collect $185 on the CC, get called away, collect $210 on the next CSP. $605 in premium across three cycles on the same stock.',
};

const BPS_DATA = {
  tagline: 'Get paid to be right about support — with a defined ceiling on your risk.',
  quote: { text: 'The goal of a successful trader is to make the best trades. Money is secondary.', attr: 'Alexander Elder' },
  why: 'A Bull Put Spread is a defined-risk, income-generating strategy for when you are bullish or neutral on a stock or index. You sell a put at one strike and simultaneously buy a put at a lower strike — creating a spread.\n\nThe spread defines your maximum loss before you ever enter the trade. You know exactly how much you can lose. No guessing. No spiral. The premium you collect is yours to keep as long as the stock stays above your short strike at expiration.\n\nThis is the strategy that unlocks premium selling for traders who want income without unlimited downside exposure.',
  right: ['You are bullish or neutral on the underlying', 'Stock is at or above a key support level', 'IV is elevated — premium is rich enough to justify the trade', 'You want defined risk — maximum loss is known before entry', '30–45 days to expiration — optimal theta decay window', 'The broader market is trending upward or sideways', 'You want to generate income without owning shares', '🟢 Short put at 0.15 delta — ~85% probability of expiring worthless. Buy one or two strikes below for protection.'],
  wrong: ['The stock is in a clear downtrend', 'Support levels have already been broken', 'IV is very low — credit too small for the risk taken', 'Earnings are approaching — avoid the binary event', 'You are not comfortable with the maximum loss amount', 'The broader market is in a sharp selloff'],
  risk: 'If the stock falls below your short put strike at expiration you will be at maximum loss — the width of the spread minus the credit received. This is fixed and known before you enter. But it is still real money.\n\nThe risk is not the spread blowing out — it is entering when the stock or market is already breaking down. Do not sell bull put spreads into falling markets hoping for a bounce. Wait for confirmation that support is holding.',
  success: 'The stock stays above your short put strike at expiration. Both puts expire worthless. You keep the full premium collected. Your defined risk never materializes.\n\nIdeal outcome: stock at $95. Sell $90/$85 bull put spread for $1.50 credit ($150 per spread). Stock closes above $90 at expiration. Keep $150. Maximum risk was $350. You never came close to it.',
};

const BCS_DATA = {
  tagline: 'Collect premium when you expect resistance to hold.',
  quote: { text: 'It is not whether you are right or wrong, but how much money you make when you are right and how much you lose when you are wrong.', attr: 'George Soros' },
  why: 'A Bear Call Spread is the mirror of the Bull Put Spread. You are bearish or neutral — you believe the stock will stay below a resistance level. You sell a call at that resistance and buy a call at a higher strike to cap your risk.\n\nYou collect a credit. If the stock stays below your short call strike through expiration, both calls expire worthless and you keep the premium.\n\nDefined risk. No shares needed. Just a clear read on where the stock is likely to stall.',
  right: ['Stock is near or at a resistance level', 'You are bearish or neutral on the underlying', 'IV is elevated — credit is meaningful', 'Stock has failed to break resistance multiple times', 'The broader market is showing weakness or is range-bound', '30–45 days to expiration — sweet spot for theta decay', 'You want defined downside risk on a bearish position', '🟢 Short call at 0.15 delta — ~85% probability of expiring worthless. Buy one or two strikes above for protection.'],
  wrong: ['Stock is in a strong uptrend breaking out above resistance', 'IV is very low — credit too small for the risk', 'Earnings approaching — gap-up risk is too high', 'You are chasing a move that has already happened', 'The broader market is in a strong bull trend'],
  risk: 'If the stock breaks above your short call strike the spread moves against you. Maximum loss is the width of the spread minus the credit. This happens when resistance fails and the stock breaks out — the exact scenario you were betting against.\n\nNever sell bear call spreads on stocks that are in strong uptrends. Resistance levels need context — a level that failed three times in a flat market is meaningful. A level that barely slowed a strong breakout is not.',
  success: 'The stock stays below your short call strike through expiration. Both calls expire worthless. You keep the full credit.\n\nIdeal outcome: stock at $102. Sell $105/$110 bear call spread for $1.20 credit ($120 per spread). Stock fails to break $105 and closes at $101 at expiration. Keep $120. Maximum risk was $380. Never touched.',
};

const IC_DATA = {
  tagline: 'Profit from a market that goes nowhere.',
  quote: { text: 'In investing, what is comfortable is rarely profitable.', attr: 'Robert Arnott' },
  why: 'An Iron Condor combines a Bull Put Spread below the market and a Bear Call Spread above it. You collect premium from both sides simultaneously. Your profit zone is the range between your two short strikes — as long as the stock stays inside that range, you win.\n\nThis is the premium seller\'s dream in a sideways market. Two income streams. Defined risk on both sides. No directional bias required.\n\nThe Iron Condor is not for volatile, trending markets. It is for stocks and indexes that are going nowhere — grinding sideways while you collect premium from both sides.',
  right: ['Stock is range-bound — not trending strongly in either direction', 'IV is elevated — premium is rich enough to justify both spreads', 'VIX is moderate to high — condors pay well in uncertain markets', 'Stock is between clear support and resistance', 'No major catalysts expected before expiration', '30–45 days to expiration', 'You want income from a neutral, non-directional position'],
  wrong: ['Stock is in a strong trend — up or down', 'IV is very low — not enough premium to justify the structure', 'Earnings are approaching — the stock will move', 'Market is highly volatile and making large daily swings', 'You need the stock to stay in a very narrow range — width too small'],
  risk: 'If the stock makes a large move in either direction and breaches one of your short strikes, you are in trouble on that side. The maximum loss on any Iron Condor is the width of the wider spread minus the total credit received.\n\nThe hidden risk: traders adjust one side when the stock moves toward it, then the stock reverses and hits the other side. Managing condors requires discipline. Respect your adjustment triggers and do not let one side run to max loss.',
  success: 'The stock meanders sideways through expiration. Both spreads expire worthless. You collect the full combined credit.\n\nIdeal outcome: SPX at 5000. Sell 4900/4880 bull put spread for $0.90. Sell 5100/5120 bear call spread for $0.85. Total credit: $1.75 ($175 per condor). SPX stays between 4900 and 5100. Keep $175. Maximum risk was $825 on either side.',
};

const ZERO_DTE_DATA = {
  tagline: 'Same-day premium. Active management required.',
  quote: { text: 'Trade what you see, not what you think.', attr: 'Trading Wisdom' },
  why: 'Zero days to expiration. The option expires at market close today. You sell a bull put spread on SPX and collect a small credit for the position staying above your short strike for just a few hours.\n\n0DTE trading is high probability — the spread only needs to stay out of the money for one trading session. But gamma is extremely high near expiration, which means a move against you near the close can be fast and significant.\n\nThis strategy is for active traders who are watching the market during trading hours and have clear rules — especially the 2x loss rule.',
  right: ['VIX is elevated — intraday premium is rich', 'Clear morning trend or range has been established', 'No major scheduled events during market hours', 'You are actively watching the position', 'Broad market is stable — not gapping violently', 'SPX is above key intraday support levels'],
  wrong: ['Fed announcement day — too much event risk', 'Gap down open — wait for stability first', 'Very low VIX — premium too small to justify risk', 'You cannot watch the position during market hours', 'CPI or major data release scheduled today', 'You are emotionally activated — not a 0DTE day'],
  risk: '0DTE gamma is the highest of any options trade. Near expiration a small move in SPX can cause a large and rapid change in your position value. These trades can go from profitable to max loss in minutes near the close.\n\nClear rules are not optional for 0DTE — they are survival. The 2x loss rule exists for exactly this reason. If your premium doubles against you, close the trade. No exceptions. No hoping. Close it.',
  success: 'SPX stays above your short strike from open to close. Your spread expires worthless at 4pm. You collect the full credit in a single trading session. Done.\n\nIdeal outcome: sell 4990/4980 bull put spread at 9:45am for $1.50 credit ($150 per spread). SPX closes above 4990. Keep $150. Took one trading session.',
};

const COLLAR_DATA = {
  tagline: 'Protect what you own without giving up the upside.',
  quote: { text: 'The first rule of an investment is do not lose. The second rule is do not forget the first rule.', attr: 'Warren Buffett' },
  why: 'A Collar is a protective strategy for existing shareholders. You own 100 shares and you are worried about a short-term pullback — but you are not ready to sell. You buy a protective put to cap your downside and sell a covered call to pay for it.\n\nThe call premium offsets the cost of the put. Done correctly, the collar can be put on for zero cost — or even a small credit. You cap your upside at the call strike and cap your downside at the put strike.\n\nThis is not about making money. It is about protecting money you already have — while staying in the position.',
  right: ['You own 100+ shares of a stock you want to keep long term', 'You are concerned about a short-term pullback', 'Earnings or a major catalyst is approaching', 'The stock has had a significant run and you want to protect gains', 'IV is elevated — put protection is worth buying', 'You want to reduce risk without paying out of pocket'],
  wrong: ['You expect the stock to make a strong move upward — the collar caps it', 'IV is very low — puts are cheap but calls pay little', 'You are willing to sell the stock — just sell instead', 'The stock pays a significant dividend you do not want disrupted', 'You are not comfortable with the call strike limiting your gains'],
  risk: 'A collar caps your upside at the call strike. If the stock runs significantly above your call, your shares will be called away at that price. You miss every dollar above the strike.\n\nThis is not a loss — but it can feel like one if the stock makes a major move. Know your call strike before you enter and make peace with it. The collar is a tool for protection, not maximum profit.',
  success: 'The stock pulls back to your put strike — your downside is fully protected. Or the stock recovers and your collar expires worthless — you keep your shares and had protection for free.\n\nIdeal outcome: own 100 shares at $78. Stock ran to $82. Concerned about pullback. Sell $85 call for $1.20, buy $79 put for $1.15. Net credit: $0.05. Protected from $79 down. Potential upside to $85. Insurance cost: nearly zero.',
};

const OVERVIEWS = {
  csp:       { overview1: CSP_DATA,   overview2: BPS_DATA },
  cc:        { overview1: CC_DATA,    overview2: BCS_DATA },
  wheel:     WHEEL_DATA,
  zdte:      ZERO_DTE_DATA,
  ironcondor: IC_DATA,
  collar:    COLLAR_DATA,
};

// ─────────────────────────────────────────────────────────────────────────────
// OVERVIEW CARD
// ─────────────────────────────────────────────────────────────────────────────
function OverviewCard({ overview, onStart, onNavigate }) {
  const [opacity, setOpacity] = useState(0);
  useEffect(() => { const t = setTimeout(() => setOpacity(1), 16); return () => clearTimeout(t); }, []);

  const divider = { height: '0.5px', background: 'rgba(201,168,76,0.1)', margin: '24px 0' };
  const secTitle = { ...serif, fontSize: '18px', fontWeight: 400, color: C.gold, margin: '0 0 12px' };
  const body = { fontSize: '14px', color: 'rgba(240,237,230,0.85)', fontFamily: "'Jost',sans-serif", lineHeight: 1.9, margin: 0, whiteSpace: 'pre-line' };

  return (
    <div style={{ background: '#111114', borderRadius: '12px', padding: '32px', maxWidth: '800px', margin: '0 auto', opacity, transition: 'opacity 0.3s ease' }}>
      {/* Tagline */}
      <p style={{ ...serif, fontSize: '20px', fontStyle: 'italic', color: C.goldL, margin: '0 0 24px', lineHeight: 1.5 }}>
        "{overview.tagline}"
      </p>

      {/* Quote */}
      <div style={{ borderLeft: `3px solid ${C.gold}`, paddingLeft: '20px', marginBottom: '24px' }}>
        <p style={{ ...serif, fontSize: '16px', fontStyle: 'italic', color: C.gold, lineHeight: 1.75, margin: '0 0 8px' }}>
          "{overview.quote.text}"
        </p>
        <span style={{ fontSize: '12px', color: C.muted, fontFamily: "'Jost',sans-serif" }}>— {overview.quote.attr}</span>
      </div>

      <div style={divider} />

      {/* Why */}
      <h3 style={secTitle}>Why use this strategy</h3>
      <p style={body}>{overview.why}</p>

      <div style={divider} />

      {/* Right */}
      <h3 style={secTitle}>Right market conditions</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {overview.right.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, lineHeight: '1.85', fontSize: '13px' }}>🟢</span>
            <span style={{ fontSize: '14px', color: 'rgba(240,237,230,0.85)', fontFamily: "'Jost',sans-serif", lineHeight: 1.8 }}>{item}</span>
          </div>
        ))}
      </div>

      <div style={divider} />

      {/* Wrong */}
      <h3 style={secTitle}>Wrong market conditions</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {overview.wrong.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, lineHeight: '1.85', fontSize: '13px' }}>🔴</span>
            <span style={{ fontSize: '14px', color: 'rgba(240,237,230,0.85)', fontFamily: "'Jost',sans-serif", lineHeight: 1.8 }}>{item}</span>
          </div>
        ))}
      </div>

      <div style={divider} />

      {/* Risk */}
      <h3 style={secTitle}>Risk you must accept</h3>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <span style={{ flexShrink: 0, lineHeight: '1.9', fontSize: '15px' }}>⚠️</span>
        <p style={{ ...body, fontStyle: 'italic' }}>{overview.risk}</p>
      </div>

      <div style={divider} />

      {/* Success */}
      <h3 style={secTitle}>What success looks like</h3>
      <p style={body}>{overview.success}</p>
      {overview.note && (
        <p style={{ fontSize: '11px', fontStyle: 'italic', fontFamily: "'Jost',sans-serif", color: 'rgba(240,237,230,0.45)', marginTop: '12px', lineHeight: 1.7 }}>{overview.note}</p>
      )}

      <div style={divider} />

      {/* Resource links */}
      {onNavigate && (
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'20px' }}>
          <button
            onClick={() => onNavigate('calc_position')}
            style={{ padding:'9px 18px', background:'transparent', border:`0.5px solid ${C.border}`, borderRadius:'7px', color:C.muted, fontSize:'12px', cursor:'pointer', fontFamily:"'Jost',sans-serif" }}
          >
            ◎ Position Size Calculator
          </button>
          <button
            onClick={() => onNavigate('ch_riskmanagement')}
            style={{ padding:'9px 18px', background:'transparent', border:`0.5px solid ${C.border}`, borderRadius:'7px', color:C.muted, fontSize:'12px', cursor:'pointer', fontFamily:"'Jost',sans-serif" }}
          >
            ◐ Risk Management Lesson
          </button>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={onStart}
        style={{ padding: '12px 28px', background: 'rgba(201,168,76,0.12)', border: '0.5px solid rgba(201,168,76,0.5)', borderRadius: '8px', color: C.goldL, fontSize: '14px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontWeight: 500 }}
      >
        Show me how it works →
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// THETA DECAY CANVAS
// ─────────────────────────────────────────────────────────────────────────────
function ThetaDecayCanvas({ height = 110 }) {
  const cvs = useRef(null);
  useEffect(() => {
    const canvas = cvs.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = C.bg3;
    ctx.fillRect(0, 0, w, h);
    const pad = { l: 8, r: 8, t: 12, b: 22 };
    const cw = w - pad.l - pad.r;
    const ch = h - pad.t - pad.b;
    const accelX = pad.l + cw * (1 - 30 / 45);
    ctx.fillStyle = 'rgba(196,122,122,0.06)';
    ctx.fillRect(accelX, pad.t, pad.l + cw - accelX, ch);
    const grad = ctx.createLinearGradient(0, pad.t, 0, pad.t + ch);
    grad.addColorStop(0, 'rgba(201,168,76,0.22)');
    grad.addColorStop(1, 'rgba(201,168,76,0.02)');
    ctx.beginPath();
    for (let i = 0; i <= cw; i++) {
      const r = 1 - i / cw;
      const val = Math.pow(r, 0.55);
      const x = pad.l + i, y = pad.t + ch - val * ch;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.lineTo(pad.l + cw, pad.t + ch);
    ctx.lineTo(pad.l, pad.t + ch);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.beginPath();
    ctx.strokeStyle = C.gold; ctx.lineWidth = 2;
    for (let i = 0; i <= cw; i++) {
      const r = 1 - i / cw;
      const val = Math.pow(r, 0.55);
      const x = pad.l + i, y = pad.t + ch - val * ch;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.strokeStyle = 'rgba(196,122,122,0.35)'; ctx.lineWidth = 0.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath(); ctx.moveTo(accelX, pad.t); ctx.lineTo(accelX, pad.t + ch); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = C.muted; ctx.font = '9px Jost, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('45 DTE', pad.l + 20, h - 5);
    ctx.fillText('0 DTE', pad.l + cw - 12, h - 5);
    ctx.fillStyle = 'rgba(196,122,122,0.7)'; ctx.textAlign = 'left'; ctx.font = '9px Jost, sans-serif';
    ctx.fillText('Decay accelerates here', accelX + 4, pad.t + 11);
  }, []);
  return <canvas ref={cvs} width={800} height={height} style={{ width: '100%', height: `${height}px`, borderRadius: '6px', display: 'block' }} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// BUYING CALLS & PUTS CHART
// ─────────────────────────────────────────────────────────────────────────────
function BuyingCallsPutsChart() {
  const [scenario, setScenario] = useState('flat');
  const SCENARIOS = {
    flat:    { label: 'Stock Stays Flat', buyerPnL: -200, sellerPnL: 200, buyerNote: 'Full premium lost — stock never moved enough.', sellerNote: 'Full $200 premium kept. This is the most common outcome.' },
    partial: { label: 'Partial Move +4%', buyerPnL: -200, sellerPnL: 200, buyerNote: 'Stock moved, but not enough. Still below the $177 breakeven. Buyer loses all $200.', sellerNote: 'Seller keeps the full $200. A 4% move wasn\'t enough.' },
    surge:   { label: 'Stock Surges +12%', buyerPnL: 710, sellerPnL: -710, buyerNote: 'Buyer wins big. Stock blew past the $177 breakeven.', sellerNote: 'Seller loses. This is exactly why we use spreads to cap our risk.' },
  };
  const sc = SCENARIOS[scenario];
  const bc = sc.buyerPnL >= 0 ? C.sage : C.rose;
  const sc2 = sc.sellerPnL >= 0 ? C.sage : C.rose;
  return (
    <div>
      <div style={{ ...eyebrow, marginBottom: '12px' }}>Stock: $170 · You buy the $175 call for $2.00 ($200 total) · Breakeven: $177</div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {Object.entries(SCENARIOS).map(([k, s]) => (
          <button key={k} style={btnS(scenario === k)} onClick={() => setScenario(k)}>{s.label}</button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
        <div style={{ ...card, borderColor: bc + '55' }}>
          <div style={{ ...eyebrow, marginBottom: '8px', color: C.muted }}>Option Buyer</div>
          <div style={{ fontSize: '13px', color: C.muted, lineHeight: 1.65, marginBottom: '14px' }}>
            Paid $200 for the right to buy 100 shares at $175. Needs the stock above $177 to profit at expiration.
          </div>
          <div style={{ fontSize: '32px', fontWeight: 500, color: bc, ...serif }}>
            {sc.buyerPnL >= 0 ? '+' : ''}${sc.buyerPnL}
          </div>
          <div style={{ fontSize: '12px', color: bc, marginTop: '6px', lineHeight: 1.5 }}>{sc.buyerNote}</div>
        </div>
        <div style={{ ...card, borderColor: sc2 + '55' }}>
          <div style={{ ...eyebrow, marginBottom: '8px', color: C.muted }}>Option Seller</div>
          <div style={{ fontSize: '13px', color: C.muted, lineHeight: 1.65, marginBottom: '14px' }}>
            Collected $200 upfront. Wins whenever the buyer loses — which is the large majority of the time.
          </div>
          <div style={{ fontSize: '32px', fontWeight: 500, color: sc2, ...serif }}>
            {sc.sellerPnL >= 0 ? '+' : ''}${sc.sellerPnL}
          </div>
          <div style={{ fontSize: '12px', color: sc2, marginTop: '6px', lineHeight: 1.5 }}>{sc.sellerNote}</div>
        </div>
      </div>
      <div style={{ ...card, background: 'rgba(201,168,76,0.05)', borderColor: 'rgba(201,168,76,0.2)', marginBottom: '20px' }}>
        <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.8, margin: 0 }}>
          {scenario === 'surge'
            ? 'The rare scenario — the stock makes a large, sustained move and the buyer profits significantly. This is exactly why premium sellers use spreads: a second option at a higher strike caps the loss in situations like this.'
            : scenario === 'partial'
            ? 'A 4% move feels meaningful — but the buyer still loses their full $200. The premium they paid creates a high bar. The seller wins even when the stock moves in the buyer\'s direction.'
            : 'The most common outcome. The stock goes nowhere, the option expires worthless, and the seller keeps the full $200. Do this 8–10 times a year and the income adds up.'}
        </p>
      </div>
      <div style={{ ...eyebrow, marginBottom: '8px' }}>Theta Decay — The Seller's Edge</div>
      <ThetaDecayCanvas height={100} />
      <p style={{ fontSize: '11px', color: C.muted, fontFamily: "'Jost',sans-serif", marginTop: '8px', lineHeight: 1.6, fontStyle: 'italic' }}>
        Every day, the option loses value. As a seller, time is working for you. As a buyer, you are racing against it.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BUYING CALLS & PUTS OVERVIEW
// ─────────────────────────────────────────────────────────────────────────────
function BuyingCallsPutsOverview({ onStart, onNavigate, onNextStrategy }) {
  const [opacity, setOpacity] = useState(0);
  useEffect(() => { const t = setTimeout(() => setOpacity(1), 16); return () => clearTimeout(t); }, []);
  const divider = { height: '0.5px', background: 'rgba(201,168,76,0.1)', margin: '28px 0' };
  const secTitle = { ...serif, fontSize: '20px', fontWeight: 400, color: C.gold, margin: '0 0 14px' };
  const body = { fontSize: '14px', color: 'rgba(240,237,230,0.85)', fontFamily: "'Jost',sans-serif", lineHeight: 1.9, margin: 0 };
  return (
    <div style={{ background: '#111114', borderRadius: '12px', padding: '32px', maxWidth: '800px', margin: '0 auto', opacity, transition: 'opacity 0.3s ease' }}>

      {/* THE KEY INSIGHT */}
      <div style={{ background: 'rgba(201,168,76,0.06)', borderLeft: `4px solid ${C.gold}`, borderRadius: '10px', padding: '28px 28px 24px', marginBottom: '32px' }}>
        <h2 style={{ ...serif, fontSize: '28px', fontWeight: 400, color: C.gold, margin: '0 0 20px', lineHeight: 1.2 }}>
          Be the Casino — Not the Gambler
        </h2>
        <p style={{ ...body, marginBottom: '20px' }}>
          Most people who buy options lose money. Not because they are bad traders — but because the math is stacked against them. The buyer needs to be right about direction, timing, and magnitude. The seller only needs to be roughly right, or even wrong in a small way, and still wins.
        </p>
        <p style={{ ...body, marginBottom: '20px' }}>
          Think about what makes a casino profitable. It does not win every hand. It does not win every spin. It wins consistently — week after week, month after month — because on every single bet, the odds tilt slightly in its favor. Over thousands of bets, those odds produce a predictable income.
        </p>
        <p style={{ ...body, marginBottom: '24px' }}>
          Option sellers are the casino. When you sell a premium, you collect money upfront and let time and probability work for you. The buyer is placing a bet. You are the house that took the other side of it.
        </p>
        <div style={{ borderLeft: `2px solid rgba(201,168,76,0.3)`, paddingLeft: '18px' }}>
          <p style={{ ...serif, fontSize: '16px', fontStyle: 'italic', color: C.goldL, lineHeight: 1.75, margin: '0 0 8px' }}>
            "Risk comes from not knowing what you're doing."
          </p>
          <span style={{ fontSize: '12px', color: C.muted, fontFamily: "'Jost',sans-serif" }}>— Warren Buffett</span>
        </div>
      </div>

      <div style={divider} />

      {/* WHY LEARN BUYING FIRST */}
      <h3 style={secTitle}>Why learn buying first</h3>
      <p style={body}>
        Every option you sell was bought by someone else. That person is your counterparty — and understanding what they need to win, and how rarely they achieve it, is what makes you a better seller. This lesson exists so you understand the instrument before you trade it, and so you never accidentally take the wrong side of a position.
      </p>

      <div style={divider} />

      {/* BUYING A CALL */}
      <h3 style={secTitle}>Buying a call — what actually happens</h3>
      <p style={{ ...body, marginBottom: '16px' }}>
        A call option gives the buyer the right — not the obligation — to purchase 100 shares at a set price (the strike) before expiration. They pay a premium upfront for that right.
      </p>
      <div style={{ background: 'rgba(122,171,138,0.07)', border: '0.5px solid rgba(122,171,138,0.2)', borderRadius: '10px', padding: '18px 20px', marginBottom: '20px' }}>
        <div style={{ ...eyebrow, marginBottom: '10px', color: C.sage }}>Real Example</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          {[
            ['Stock price', '$170'],
            ['Strike (the bet)', '$175 call'],
            ['Premium paid', '$2.00 per share'],
            ['Total cost', '$200 (×100 shares)'],
            ['Breakeven at expiry', '$177 (strike + premium)'],
            ['What must happen', 'Stock above $177 to profit'],
          ].map(([l, v]) => (
            <div key={l}>
              <div style={{ fontSize: '11px', color: C.muted, fontFamily: "'Jost',sans-serif", marginBottom: '2px' }}>{l}</div>
              <div style={{ fontSize: '14px', color: C.text, fontFamily: "'Jost',sans-serif", fontWeight: 500 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '0.5px solid rgba(240,237,230,0.07)', fontSize: '13px', color: C.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.7 }}>
          If the stock closes at $174 on expiration — even though it moved up $4 — the buyer loses their entire $200. It never crossed the $177 breakeven.
        </div>
      </div>

      {/* BUYING A PUT */}
      <h3 style={secTitle}>Buying a put — what actually happens</h3>
      <p style={{ ...body, marginBottom: '16px' }}>
        A put option gives the buyer the right to sell 100 shares at the strike price. Put buyers profit when a stock falls. Investors also use puts as insurance on shares they already own.
      </p>
      <div style={{ background: 'rgba(196,122,122,0.07)', border: '0.5px solid rgba(196,122,122,0.2)', borderRadius: '10px', padding: '18px 20px', marginBottom: '20px' }}>
        <div style={{ ...eyebrow, marginBottom: '10px', color: C.rose }}>Real Example</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          {[
            ['Stock price', '$170'],
            ['Strike (the bet)', '$165 put'],
            ['Premium paid', '$1.80 per share'],
            ['Total cost', '$180 (×100 shares)'],
            ['Breakeven at expiry', '$163.20 (strike − premium)'],
            ['What must happen', 'Stock below $163.20 to profit'],
          ].map(([l, v]) => (
            <div key={l}>
              <div style={{ fontSize: '11px', color: C.muted, fontFamily: "'Jost',sans-serif", marginBottom: '2px' }}>{l}</div>
              <div style={{ fontSize: '14px', color: C.text, fontFamily: "'Jost',sans-serif", fontWeight: 500 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '0.5px solid rgba(240,237,230,0.07)', fontSize: '13px', color: C.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.7 }}>
          If the stock drops to $166 at expiration — a meaningful 2.4% decline — the buyer still loses their full $180. The stock didn't fall far enough to cross the breakeven.
        </div>
      </div>

      <div style={divider} />

      {/* THETA */}
      <h3 style={secTitle}>Theta — the seller's built-in edge</h3>
      <p style={{ ...body, marginBottom: '16px' }}>
        Every single day, an option loses a small amount of its value — even if the stock doesn't move at all. This erosion accelerates sharply in the final 30 days before expiration. Option buyers are fighting time decay from the moment they enter. Option sellers are collecting it.
      </p>
      <ThetaDecayCanvas height={110} />
      <p style={{ fontSize: '12px', color: C.muted, fontFamily: "'Jost',sans-serif", marginTop: '10px', lineHeight: 1.65, fontStyle: 'italic' }}>
        The red zone shows where decay accelerates — the final 30 days of an option's life. As a seller, theta works in your favor every market day. The clock is your ally, not your enemy.
      </p>

      <div style={divider} />

      {/* WHEN BUYING MAKES SENSE */}
      <h3 style={secTitle}>When buying makes sense</h3>
      <p style={{ ...body, marginBottom: '16px' }}>
        Buying options isn't always wrong — it just requires a different mindset and a specific reason. Here are the situations where buying is the right tool:
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
        {[
          { label: 'Hedging shares you own', desc: 'Buying puts against a long stock position protects you from a sharp downturn — this is legitimate, strategic use.' },
          { label: 'High-conviction directional catalyst', desc: 'Earnings announcements, FDA decisions, macro events — when you have a specific thesis with a short time window.' },
          { label: 'Defined risk on a large move', desc: 'A call or put costs less than buying shares outright and defines your maximum loss at the premium paid.' },
          { label: 'You understand the math going in', desc: 'A buyer who knows they need a 6% move in 3 weeks and prices that into their plan is making an informed bet — not gambling.' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '12px 14px', background: 'rgba(240,237,230,0.03)', borderRadius: '8px' }}>
            <span style={{ flexShrink: 0, fontSize: '11px', color: C.gold, marginTop: '3px' }}>→</span>
            <div>
              <div style={{ fontSize: '13px', color: C.text, fontFamily: "'Jost',sans-serif", fontWeight: 500, marginBottom: '3px' }}>{item.label}</div>
              <div style={{ fontSize: '13px', color: C.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.65 }}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={divider} />

      {/* RUBBER BAND EXTREMES */}
      <h3 style={secTitle}>When buying makes sense in this program</h3>
      <div style={{ background: 'rgba(201,168,76,0.06)', borderLeft: `4px solid ${C.gold}`, borderRadius: '10px', padding: '22px 24px', marginBottom: '20px' }}>
        <p style={{ ...body, marginBottom: '16px' }}>
          The rubber band has a breaking point. When RSI is at 25, price is at the lower Bollinger Band, AND price is at a major support level — the rubber band has stretched to maximum. At these extremes, buying a call or put with clear intent and a defined exit is a precise tool — not speculation.
        </p>
        <p style={{ ...body, marginBottom: '16px' }}>
          This is different from buying options randomly hoping for a big move. This is identifying maximum stretch and taking a defined position on the snapback.
        </p>
        <p style={{ ...body, marginBottom: '0' }}>
          For everything else in normal market conditions — sell premium. Be the casino. The rubber band will work for you.
        </p>
      </div>

      <div style={divider} />

      <h3 style={secTitle}>Cost Is Everything When Buying Options</h3>
      <p style={{ ...body, marginBottom: '14px' }}>
        When you buy an option, you are paying for the right to profit from a move. The cost of that right — the premium — is set by the market's fear level at the moment you buy it.
      </p>
      <p style={{ ...body, marginBottom: '14px' }}>
        When the market is calm and VIX is low, options are cheap. Institutional investors buy puts as cheap insurance when fear is low — not when they are already panicking. A smart buyer follows the same logic: buy options when they are cheap, not when everyone else is already scared and prices are inflated.
      </p>
      <p style={{ ...body, marginBottom: '20px' }}>
        The rubber band principle applies to cost as well as direction. When the rubber band has stretched to maximum and fear is elevated — options are expensive. The moment of maximum stretch is often when the reversal begins and IV crushes back down. A buyer who enters at the exact extreme may be right about direction and still lose money because the option cost twice what it should have.
      </p>
      <div style={{ background: 'rgba(196,122,122,0.06)', borderLeft: `3px solid ${C.rose}`, borderRadius: '8px', padding: '16px 20px', marginBottom: '20px' }}>
        <p style={{ ...body, fontSize: '13px', marginBottom: '0' }}>
          <strong style={{ color: C.rose }}>The cost trap:</strong> Buying a call when VIX is 35 because "the market must bounce" is a beginner mistake. You are buying at peak fear — peak price. Even if the market bounces exactly as you predicted, IV crush can cut your option's value in half on the way back up.
        </p>
      </div>

      <div style={divider} />

      {/* CTA */}
      <p style={{ ...body, marginBottom: '24px' }}>
        Now that you understand what the buyer is doing — and why the math favors the seller — it's time to learn how to be the seller. The Cash Secured Put is where we start: a strategy that generates consistent income while giving you the chance to buy quality stocks at a discount.
      </p>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
        <button onClick={onStart} style={{ padding: '12px 24px', background: 'rgba(201,168,76,0.1)', border: '0.5px solid rgba(201,168,76,0.4)', borderRadius: '8px', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}>
          See the buyer vs. seller animation →
        </button>
        {onNextStrategy && (
          <button onClick={onNextStrategy} style={{ padding: '12px 28px', background: 'rgba(201,168,76,0.15)', border: '0.5px solid rgba(201,168,76,0.6)', borderRadius: '8px', color: C.goldL, fontSize: '14px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontWeight: 500 }}>
            Now learn how to be the seller →
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMBINED OVERVIEW CARD
// ─────────────────────────────────────────────────────────────────────────────
function CombinedOverviewCard({ chart, overview1, overview2, onStart, onNavigate }) {
  const [opacity, setOpacity] = useState(0);
  useEffect(() => { const t = setTimeout(() => setOpacity(1), 16); return () => clearTimeout(t); }, []);
  const divider = { height: '0.5px', background: 'rgba(201,168,76,0.1)', margin: '22px 0' };
  const secTitle = { ...serif, fontSize: '18px', fontWeight: 400, color: C.gold, margin: '0 0 12px' };
  const body = { fontSize: '14px', color: 'rgba(240,237,230,0.85)', fontFamily: "'Jost',sans-serif", lineHeight: 1.9, margin: 0, whiteSpace: 'pre-line' };

  const renderOv = (ov) => (
    <>
      <p style={{ ...serif, fontSize: '19px', fontStyle: 'italic', color: C.goldL, margin: '0 0 20px', lineHeight: 1.5 }}>"{ov.tagline}"</p>
      <div style={{ borderLeft: `3px solid ${C.gold}`, paddingLeft: '18px', marginBottom: '20px' }}>
        <p style={{ ...serif, fontSize: '15px', fontStyle: 'italic', color: C.gold, lineHeight: 1.75, margin: '0 0 6px' }}>"{ov.quote.text}"</p>
        <span style={{ fontSize: '12px', color: C.muted, fontFamily: "'Jost',sans-serif" }}>— {ov.quote.attr}</span>
      </div>
      <h3 style={secTitle}>Why use this strategy</h3>
      <p style={body}>{ov.why}</p>
      <div style={divider} />
      <h3 style={secTitle}>Right market conditions</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {ov.right.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, lineHeight: '1.85', fontSize: '13px' }}>🟢</span>
            <span style={{ fontSize: '14px', color: 'rgba(240,237,230,0.85)', fontFamily: "'Jost',sans-serif", lineHeight: 1.8 }}>{item}</span>
          </div>
        ))}
      </div>
      <div style={divider} />
      <h3 style={secTitle}>Wrong market conditions</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {ov.wrong.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, lineHeight: '1.85', fontSize: '13px' }}>🔴</span>
            <span style={{ fontSize: '14px', color: 'rgba(240,237,230,0.85)', fontFamily: "'Jost',sans-serif", lineHeight: 1.8 }}>{item}</span>
          </div>
        ))}
      </div>
      <div style={divider} />
      <h3 style={secTitle}>Risk you must accept</h3>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <span style={{ flexShrink: 0, lineHeight: '1.9', fontSize: '15px' }}>⚠️</span>
        <p style={{ ...body, fontStyle: 'italic' }}>{ov.risk}</p>
      </div>
      <div style={divider} />
      <h3 style={secTitle}>What success looks like</h3>
      <p style={body}>{ov.success}</p>
      {ov.note && (
        <p style={{ fontSize: '11px', fontStyle: 'italic', fontFamily: "'Jost',sans-serif", color: 'rgba(240,237,230,0.45)', marginTop: '12px', lineHeight: 1.7 }}>{ov.note}</p>
      )}
    </>
  );

  return (
    <div style={{ background: '#111114', borderRadius: '12px', padding: '32px', maxWidth: '800px', margin: '0 auto', opacity, transition: 'opacity 0.3s ease' }}>
      <div style={{ ...eyebrow, marginBottom: '14px' }}>{chart.label1}</div>
      {renderOv(overview1)}

      <div style={{ margin: '32px 0', padding: '22px 24px', background: 'rgba(201,168,76,0.06)', border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '10px' }}>
        <div style={{ ...eyebrow, marginBottom: '8px' }}>Taking It Further</div>
        <p style={{ fontSize: '14px', color: C.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.85, margin: 0 }}>
          {chart.id === 'csp'
            ? 'The Cash Secured Put is the foundation. The Bull Put Spread is the evolution. Same income-generating logic — but you add a long put at a lower strike to define your maximum loss. Less capital required. Defined downside. The natural upgrade when you want to trade more size or reduce exposure.'
            : 'The Covered Call requires owning shares. The Bear Call Spread does not. Same bearish or neutral logic, same premium income — but defined risk and no share ownership required. Use covered calls on positions you already hold; use bear call spreads when you want the same income without the capital commitment.'}
        </p>
      </div>

      <div style={{ ...eyebrow, marginBottom: '14px' }}>{chart.label2}</div>
      {renderOv(overview2)}

      <div style={{ margin: '28px 0 0', padding: '20px 24px', background: 'rgba(122,171,138,0.05)', border: `0.5px solid rgba(122,171,138,0.12)`, borderRadius: '10px' }}>
        <div style={{ ...eyebrow, marginBottom: '10px', color: C.sage }}>Side by Side</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: C.text, fontFamily: "'Jost',sans-serif", marginBottom: '6px' }}>{chart.label1}</div>
            <div style={{ fontSize: '12px', color: C.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.7 }}>
              {chart.id === 'csp' ? 'Requires full capital to cover 100 shares. Higher premium. Unlimited downside risk below the strike. Simpler structure — just one leg.' : 'Requires owning 100 shares. Income on shares you already hold. Caps upside at the call strike. No extra capital beyond your existing position.'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: C.text, fontFamily: "'Jost',sans-serif", marginBottom: '6px' }}>{chart.label2}</div>
            <div style={{ fontSize: '12px', color: C.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.7 }}>
              {chart.id === 'csp' ? 'Defined maximum loss. Lower capital requirement. Slightly less premium. Two legs — requires management of both positions.' : 'No shares required. Defined maximum loss. Two legs to manage. Works in any bearish or neutral market environment.'}
            </div>
          </div>
        </div>
      </div>

      {onNavigate && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', margin: '24px 0 20px' }}>
          <button onClick={() => onNavigate('calc_position')} style={{ padding: '9px 18px', background: 'transparent', border: `0.5px solid ${C.border}`, borderRadius: '7px', color: C.muted, fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}>
            ◎ Position Size Calculator
          </button>
          <button onClick={() => onNavigate('ch_riskmanagement')} style={{ padding: '9px 18px', background: 'transparent', border: `0.5px solid ${C.border}`, borderRadius: '7px', color: C.muted, fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}>
            ◐ Risk Management Lesson
          </button>
        </div>
      )}
      <button onClick={onStart} style={{ padding: '12px 28px', background: 'rgba(201,168,76,0.12)', border: '0.5px solid rgba(201,168,76,0.5)', borderRadius: '8px', color: C.goldL, fontSize: '14px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontWeight: 500 }}>
        Show me how it works →
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TEASER CARD (locked Tier 2)
// ─────────────────────────────────────────────────────────────────────────────
function ZDTELockedCard({ onSelectCsp, onNavigate }) {
  const reqCards = [
    { icon: '📊', title: 'Paper Trading', text: '30+ paper trades logged with consistent position sizing and risk management' },
    { icon: '🎯', title: 'Win Rate', text: 'Consistent results on CSP and Covered Call trades demonstrating strategy mastery' },
    { icon: '🛡', title: 'Risk Discipline', text: 'No trades exceeding 2% portfolio risk. Daily max loss rule followed consistently' },
    { icon: '🧠', title: 'Emotional Readiness', text: 'Demonstrated ability to follow rules under pressure. Inner Work practice established' },
  ];
  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', background: C.bg2, border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
      <div style={{ fontSize: '28px', color: C.gold, marginBottom: '16px' }}>🔒</div>
      <h2 style={{ ...serif, fontSize: '32px', fontWeight: 400, color: C.gold, margin: '0 0 8px' }}>0DTE Spreads</h2>
      <p style={{ ...serif, fontSize: '18px', fontStyle: 'italic', color: C.muted, margin: '0 0 24px', lineHeight: 1.5 }}>This is not where you start. This is where you arrive.</p>
      <p style={{ fontSize: '14px', color: 'rgba(240,237,230,0.8)', fontFamily: "'Jost',sans-serif", lineHeight: 1.9, maxWidth: '560px', margin: '0 auto 32px', textAlign: 'left', whiteSpace: 'pre-line' }}>
        {`0DTE trading is one of the most exciting and demanding strategies available to options traders. Same-day premium. Fast decisions. High gamma. Active management required during market hours.

This is not where you begin your journey. This is where you arrive after you have mastered the foundation — after selling puts and calls has become second nature, after you have logged real paper trades and learned how your emotions respond to positions moving against you.

0DTE requires everything the foundation teaches — risk management, emotional regulation, spread mechanics, gamma awareness — all applied in real time under market hours pressure.

When your mentor determines you are ready — when your foundation trading shows the discipline, consistency, and emotional regulation that 0DTE demands — this strategy will unlock.`}
      </p>
      <div style={{ height: '0.5px', background: 'rgba(201,168,76,0.15)', marginBottom: '24px' }} />
      <h3 style={{ ...serif, fontSize: '20px', fontWeight: 400, color: C.gold, margin: '0 0 16px' }}>What You Are Building Toward</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '24px', textAlign: 'left' }}>
        {reqCards.map(({ icon, title, text }) => (
          <div key={title} style={{ background: C.bg3, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '8px', padding: '16px' }}>
            <div style={{ fontSize: '20px', marginBottom: '6px' }}>{icon}</div>
            <div style={{ fontSize: '13px', color: C.gold, fontFamily: "'Jost',sans-serif", fontWeight: 500, marginBottom: '4px' }}>{title}</div>
            <div style={{ fontSize: '12px', color: C.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.6 }}>{text}</div>
          </div>
        ))}
      </div>
      <div style={{ height: '0.5px', background: 'rgba(201,168,76,0.15)', marginBottom: '24px' }} />
      <div style={{ borderLeft: `3px solid ${C.gold}`, background: 'rgba(201,168,76,0.04)', borderRadius: '0 8px 8px 0', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
        <p style={{ ...serif, fontSize: '16px', fontStyle: 'italic', color: C.gold, lineHeight: 1.7, margin: 0 }}>
          "In trading patience is not just a virtue — it is a strategy. The traders who master the foundation before advancing are the ones who survive long enough to thrive."
        </p>
      </div>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button style={{ ...btnG, padding: '12px 22px', fontSize: '13px' }} onClick={onSelectCsp}>Review Foundation Strategies →</button>
        <button style={{ ...btnGhost, padding: '12px 20px', fontSize: '13px' }} onClick={() => onNavigate && onNavigate('chapter_ch08')}>Go to Chapter 8 — 0DTE →</button>
      </div>
      <p style={{ fontSize: '11px', color: C.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginTop: '16px' }}>
        Your mentor will unlock this when your foundation trading is ready.
      </p>
    </div>
  );
}

function TeaserCard({ chart, unlockInput, setUnlockInput, unlockError, onUnlock }) {
  return (
    <div style={{ maxWidth: '580px', margin: '0 auto', padding: '36px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '38px', marginBottom: '14px', opacity: 0.55 }}>🔒</div>
      <div style={{ ...eyebrow, marginBottom: '10px' }}>Tier 2 — Intermediate</div>
      <h3 style={{ ...serif, fontSize: '24px', color: C.text, margin: '0 0 16px', fontWeight: 400 }}>{chart.label}</h3>
      <p style={{ fontSize: '14px', color: C.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.85, margin: '0 auto 28px', maxWidth: '460px' }}>
        {chart.teaser}
      </p>
      <div style={{ background: C.bg3, border: `0.5px solid ${C.border}`, borderRadius: '10px', padding: '22px', textAlign: 'left', maxWidth: '380px', margin: '0 auto' }}>
        <div style={{ fontSize: '13px', color: C.muted, fontFamily: "'Jost',sans-serif", marginBottom: '12px' }}>
          Enter your mentor's unlock code to access all Tier 2 strategies:
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            value={unlockInput}
            onChange={e => setUnlockInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onUnlock()}
            placeholder="TIER2-XXXXXX"
            style={{ flex: 1, padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: `0.5px solid ${unlockError ? C.rose : C.border}`, borderRadius: '7px', color: C.text, fontSize: '13px', fontFamily: "'Jost',sans-serif", outline: 'none' }}
          />
          <button onClick={onUnlock} style={{ ...btnG, padding: '9px 16px', flexShrink: 0 }}>Unlock →</button>
        </div>
        {unlockError && (
          <div style={{ fontSize: '12px', color: C.rose, fontFamily: "'Jost',sans-serif", marginTop: '8px' }}>
            Incorrect code. Ask your mentor for the Tier 2 unlock code.
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function InteractiveStrategyCharts({ chapterId, onBack, backLabel, onNavigate, tier2Unlocked = false, unlockCode, onTier2Unlock }) {
  const defaultId = chapterId && CHART_MAP[chapterId] ? CHART_MAP[chapterId] : null;
  const [active, setActive] = useState(defaultId);
  const [showOverview, setShowOverview] = useState(true);
  const [combinedPart, setCombinedPart] = useState(1);
  const [unlockInput, setUnlockInput] = useState('');
  const [unlockError, setUnlockError] = useState(false);

  useEffect(() => {
    try {
      const sel = localStorage.getItem('tlaw_strategy_selected');
      if (sel) { setActive(sel); localStorage.removeItem('tlaw_strategy_selected'); }
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => { setShowOverview(true); setCombinedPart(1); }, [active]);

  const activeChart = active ? CHARTS.find(c => c.id === active) : null;
  const tier1 = CHARTS.filter(c => c.tier === 1);
  const tier2 = CHARTS.filter(c => c.tier === 2);

  const handleUnlock = () => {
    if (unlockInput.trim() === unlockCode) {
      onTier2Unlock && onTier2Unlock();
      setUnlockError(false);
      setUnlockInput('');
    } else {
      setUnlockError(true);
    }
  };

  let chartContent = null;
  if (active === 'buying') {
    chartContent = showOverview
      ? <BuyingCallsPutsOverview onStart={() => setShowOverview(false)} onNavigate={onNavigate} onNextStrategy={() => { setActive('csp'); setShowOverview(true); }} />
      : <BuyingCallsPutsChart />;
  } else if (activeChart?.combined) {
    const ovData = OVERVIEWS[active];
    if (showOverview) {
      chartContent = (
        <CombinedOverviewCard
          chart={activeChart}
          overview1={ovData?.overview1}
          overview2={ovData?.overview2}
          onStart={() => setShowOverview(false)}
          onNavigate={onNavigate}
        />
      );
    } else {
      const Part = combinedPart === 1 ? activeChart.component1 : activeChart.component2;
      chartContent = (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button style={btnS(combinedPart === 1)} onClick={() => setCombinedPart(1)}>{activeChart.label1}</button>
            <button style={btnS(combinedPart === 2)} onClick={() => setCombinedPart(2)}>{activeChart.label2}</button>
          </div>
          <Part />
        </div>
      );
    }
  } else if (activeChart?.tier === 2 && !tier2Unlocked) {
    if (active === 'zdte') {
      chartContent = (
        <ZDTELockedCard
          onSelectCsp={() => { setActive('csp'); setShowOverview(true); }}
          onNavigate={onNavigate}
        />
      );
    } else {
      chartContent = (
        <TeaserCard
          chart={activeChart}
          unlockInput={unlockInput}
          setUnlockInput={setUnlockInput}
          unlockError={unlockError}
          onUnlock={handleUnlock}
        />
      );
    }
  } else {
    const overview = active ? OVERVIEWS[active] : null;
    const ChartComp = activeChart?.component ?? (() => <PlaceholderChart label={activeChart?.label ?? ''} />);
    if (showOverview && overview) {
      chartContent = <OverviewCard key={active} overview={overview} onStart={() => setShowOverview(false)} onNavigate={onNavigate} />;
    } else {
      chartContent = <ChartComp />;
    }
  }

  return (
    <div style={{ background: C.bg, minHeight: '100vh' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        {onBack && <button style={{ ...btnGhost, fontSize: '12px' }} onClick={onBack}>{backLabel || '← Back'}</button>}
        <div>
          <div style={{ ...eyebrow, marginBottom: '2px' }}>Strategy Charts</div>
          <h2 style={{ ...serif, fontSize: '26px', fontWeight: 500, color: C.text, margin: 0 }}>Strategy Charts — Interactive Simulations</h2>
        </div>
      </div>

      {/* Tier 1 selector */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '10px' }}>
          <div style={{ ...eyebrow, letterSpacing: '0.14em', marginBottom: '3px' }}>TIER 1 — FOUNDATION</div>
          <p style={{ fontSize: '11px', color: C.muted, fontFamily: "'Jost',sans-serif", margin: 0, lineHeight: 1.5 }}>Master these in paper trading before advancing. Each one builds on the last.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', width: '100%' }}>
          {tier1.map(ch => {
            const isActive = active === ch.id;
            const isBuying = ch.id === 'buying';
            const badge = isBuying
              ? { label: 'START HERE — FOUNDATION', bg: 'rgba(201,168,76,0.12)', border: 'rgba(201,168,76,0.35)', color: C.gold }
              : { label: 'SELLING STRATEGY', bg: 'rgba(122,171,138,0.15)', border: 'rgba(122,171,138,0.3)', color: C.sage };
            return (
              <button key={ch.id} onClick={() => setActive(ch.id)} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', background: isActive ? 'rgba(201,168,76,0.1)' : C.bg2, border: `0.5px solid ${isActive ? 'rgba(201,168,76,0.4)' : C.border}`, borderRadius: '9px', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontSize: '18px', color: isActive ? C.gold : C.muted, flexShrink: 0, paddingTop: '2px' }}>{ch.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: isActive ? C.text : C.muted }}>{ch.label}</div>
                  <div style={{ fontSize: '10px', color: C.dim, marginBottom: '5px' }}>{ch.sub}</div>
                  <span style={{ fontSize: '9px', fontFamily: "'Jost',sans-serif", fontWeight: 600, letterSpacing: '0.08em', padding: '2px 6px', borderRadius: '4px', background: badge.bg, border: `0.5px solid ${badge.border}`, color: badge.color }}>{badge.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tier 2 selector */}
      <div style={{ marginBottom: '8px' }}>
        <div style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '3px' }}>
            <div style={{ ...eyebrow, letterSpacing: '0.14em' }}>TIER 2 — INTERMEDIATE</div>
            {!tier2Unlocked && <span style={{ fontSize: '14px', lineHeight: 1 }}>🔒</span>}
          </div>
          <p style={{ fontSize: '11px', color: C.muted, fontFamily: "'Jost',sans-serif", margin: 0, lineHeight: 1.5 }}>
            {tier2Unlocked ? 'Advanced strategies for consistent foundation traders.' : 'Unlocked by your mentor when your foundation trading shows consistent results.'}
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', width: '100%' }}>
          {tier2.map(ch => {
            const isActive = active === ch.id;
            const locked = !tier2Unlocked;
            return (
              <button key={ch.id} onClick={() => setActive(ch.id)} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px 12px', background: isActive ? 'rgba(201,168,76,0.08)' : C.bg2, border: `0.5px solid ${isActive ? 'rgba(201,168,76,0.3)' : C.border}`, borderRadius: '9px', cursor: 'pointer', textAlign: 'left', opacity: locked ? 0.65 : 1 }}>
                <span style={{ fontSize: '16px', color: locked ? C.dim : isActive ? C.gold : C.muted, flexShrink: 0, paddingTop: '2px' }}>
                  {locked ? '🔒' : ch.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '11px', fontWeight: 500, color: isActive ? C.text : C.muted }}>{ch.label}</div>
                  <div style={{ fontSize: '10px', color: C.dim, marginBottom: '5px' }}>{ch.sub}</div>
                  <span style={{ fontSize: '9px', fontFamily: "'Jost',sans-serif", fontWeight: 600, letterSpacing: '0.08em', padding: '2px 6px', borderRadius: '4px', background: 'rgba(122,171,138,0.15)', border: '0.5px solid rgba(122,171,138,0.3)', color: C.sage }}>SELLING STRATEGY</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <p style={{ fontSize: '12px', color: C.muted, fontStyle: 'italic', fontFamily: "'Jost',sans-serif", margin: '0 0 20px', lineHeight: 1.6 }}>
        The four Tier 1 strategies are a complete income system. Most professional traders never need anything beyond them.
      </p>

      {/* Active chart area */}
      {activeChart ? (
        <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '12px', padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '16px', paddingBottom: '14px', borderBottom: `0.5px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '22px', color: C.gold }}>{activeChart.tier === 2 && !tier2Unlocked ? '🔒' : activeChart.icon}</span>
              <div>
                <h3 style={{ ...serif, fontSize: '22px', fontWeight: 500, color: C.text, margin: 0 }}>{activeChart.label}</h3>
                <p style={{ fontSize: '12px', color: C.muted, margin: '2px 0 0' }}>{activeChart.sub}</p>
              </div>
            </div>
            {!showOverview && !(activeChart.tier === 2 && !tier2Unlocked) && (
              <button style={{ ...btnGhost, fontSize: '12px' }} onClick={() => setShowOverview(true)}>← Back to overview</button>
            )}
          </div>
          {chartContent}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: C.muted, fontSize: '14px' }}>
          ← Select a strategy above to open its interactive chart
        </div>
      )}

      {/* Mastery closing card */}
      <div style={{ marginTop: '28px', background: 'rgba(201,168,76,0.04)', border: `0.5px solid rgba(201,168,76,0.1)`, borderRadius: '12px', padding: '28px 32px' }}>
        <div style={{ ...eyebrow, marginBottom: '10px' }}>Mastery Over Complexity</div>
        <h3 style={{ ...serif, fontSize: '22px', color: C.text, margin: '0 0 14px', fontWeight: 400 }}>The foundation is everything.</h3>
        <p style={{ fontSize: '14px', color: 'rgba(240,237,230,0.75)', fontFamily: "'Jost',sans-serif", lineHeight: 1.9, margin: '0 0 18px' }}>
          Professional traders do not win because they trade more strategies. They win because they understand a few strategies deeply — the mechanics, the psychology, the edge, the risk. The Tier 1 strategies in this program have generated consistent income for professional traders for decades.
        </p>
        <p style={{ ...serif, fontSize: '16px', fontStyle: 'italic', color: C.gold, lineHeight: 1.7, margin: '0 0 6px' }}>
          "The most important single thing I have learned is that you do not have to be everywhere. You just have to be right once — and patient always."
        </p>
        <span style={{ fontSize: '12px', color: C.muted, fontFamily: "'Jost',sans-serif" }}>— Jesse Livermore</span>
      </div>

      {/* Risk Management resource card */}
      <div style={{ marginTop: '20px', background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '12px', padding: '24px 28px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <span style={{ fontSize: '28px', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}>◐</span>
          <div style={{ flex: 1 }}>
            <h3 style={{ ...serif, fontSize: '20px', fontWeight: 500, color: C.text, margin: '0 0 8px' }}>Risk Management</h3>
            <p style={{ fontSize: '14px', color: 'rgba(240,237,230,0.75)', fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: '0 0 18px' }}>
              Every strategy in this program runs on the same foundation: defined risk, correct position sizing, and knowing your maximum loss before you enter.
            </p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {onNavigate && (
                <>
                  <button onClick={() => onNavigate('ch_riskmanagement')} style={{ padding: '10px 20px', background: 'rgba(201,168,76,0.1)', border: `0.5px solid rgba(201,168,76,0.4)`, borderRadius: '8px', color: C.goldL, fontSize: '13px', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}>
                    Risk Management Lesson →
                  </button>
                  <button onClick={() => onNavigate('calc_position')} style={{ ...btnGhost, fontSize: '13px' }}>
                    Position Size Calculator
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reading the Market — Advanced Concepts card */}
      {onNavigate && (
        <div style={{ marginTop: '16px', background: C.bg2, border: `0.5px solid ${tier2Unlocked ? C.border : 'rgba(201,168,76,0.08)'}`, borderRadius: '12px', padding: '24px 28px', opacity: tier2Unlocked ? 1 : 0.75 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <span style={{ fontSize: '24px', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}>{tier2Unlocked ? '◈' : '🔒'}</span>
            <div style={{ flex: 1 }}>
              {tier2Unlocked ? (
                <>
                  <div style={{ ...eyebrow, marginBottom: '4px', color: C.gold }}>Advanced Concepts</div>
                  <h3 style={{ ...serif, fontSize: '20px', fontWeight: 500, color: C.text, margin: '0 0 8px' }}>Ready to go deeper?</h3>
                  <p style={{ fontSize: '14px', color: 'rgba(240,237,230,0.75)', fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: '0 0 16px' }}>
                    Learn to read supply and demand zones, open interest levels, and momentum signals simultaneously — the way experienced traders see a chart.
                  </p>
                  <button onClick={() => onNavigate('adv_reading')} style={{ padding: '10px 22px', background: 'rgba(201,168,76,0.12)', border: `0.5px solid rgba(201,168,76,0.5)`, borderRadius: '8px', color: C.goldL, fontSize: '13px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontWeight: 500 }}>
                    Open Reading the Market →
                  </button>
                </>
              ) : (
                <>
                  <div style={{ ...eyebrow, marginBottom: '4px', color: 'rgba(201,168,76,0.5)' }}>Advanced Concepts — Locked</div>
                  <h3 style={{ ...serif, fontSize: '20px', fontWeight: 500, color: C.muted, margin: '0 0 8px' }}>Reading the Market</h3>
                  <p style={{ fontSize: '13px', color: C.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: 0 }}>
                    Unlocks with Tier 2 access. Learn to read supply and demand, open interest, and momentum simultaneously.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
